import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Reorder, useDragControls } from "framer-motion";
import {
  Plus,
  AlertTriangle,
  X,
  GripVertical,
  Archive,
  ArchiveRestore,
  User,
  Tag,
  Users,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronRight,
  ChevronDown,
  Printer,
  ListChecks,
  FileSpreadsheet,
  FileText,
  Link2,
  ExternalLink,
  Search,
  Trash2,
  ClipboardPaste,
  Minimize2,
} from "lucide-react";
import { tasksApi, teamApi, taskCategoriesApi, reminderConfigApi, taskAttachmentsApi, subtaskDatePrefsApi } from "../lib/api";
import {
  openDetachedPanel,
  closeDetachedPanel,
  getDetachedPanels,
  subscribeDetachedPanels,
  placePanelAt,
  openDetachedPanelsBatch,
  closeAllDetachedPanels,
} from "../lib/detachedPanels";
import { LayoutsMenu } from "./LayoutsMenu";
import { UndoMenu } from "./UndoMenu";
import { registerLayoutBridge } from "../lib/layoutBridge";
import { registerWindowCollector } from "../lib/windowCollectors";
import { CollectAllButton } from "./CollectAllButton";
import { kirliMi, KIRLI_UYARI } from "../lib/formDirty";
import { useFormDraft } from "../lib/useFormDraft";
import { taslakOku } from "../lib/formDrafts";
import { DraftRestoreBar } from "./DraftRestoreBar";
import {
  layoutsApi,
  currentViewport,
  fitGeom,
  readTaskGeom,
  readPanelGeom,
  writeTaskGeom,
  writePanelGeom,
} from "../lib/windowLayouts";
import { CategoryContextMenu } from "./CategoryContextMenu";
import { isOutsideCatPanel, CategoryDndProvider, SortableCategoryRow } from "./CategorySortable";
import { getDescendantIds, getCategoryPath, flattenTree } from "../lib/categoryTree";
import { flattenSubs, updateSubById, findSubById, replaceChildrenById } from "../lib/subtaskTree";
import { move } from "@dnd-kit/helpers";
import { getCatTreeExpandedSet } from "../lib/catTreePrefs";
import { getCatFilterExpandedSet, setCatFilterExpanded, saveCatFilterExpandedSet } from "../lib/catFilterPrefs";
import { LOCK_KEY_LABELS, LOCK_KEY_ORDER } from "../lib/taskLocks";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
// Faz 9 CP5 — extracted helpers (behavior unchanged).
import {
  REMINDER_DAY_CHOICES,
  ACTION_LOCK_MAP,
  LOCK_AUDIT_EVENT_META,
  formatAuditPayload,
  hasActiveUnlock,
  canManageLocks,
  isActionLocked,
  isOverdue,
  resolveThreshold,
  dueSoonLayer,
  statusStyle,
  playReminderBeep,
} from "../lib/taskHelpers";
import { UnlockOtpModal } from "./tasks/UnlockOtpModal";
import { ReassignModal } from "./tasks/ReassignModal";
import { EditTaskModal } from "./tasks/EditTaskModal";
import { MultiAssigneeSelect } from "./tasks/MultiAssigneeSelect";
import { PendingAttachments } from "./tasks/PendingAttachments";
import { PendingSubtasks } from "./tasks/PendingSubtasks";
import { PersonFilterSelect } from "./tasks/PersonFilterSelect";
import CategorySelect from "./tasks/CategorySelect";
import { SerialDateFields } from "./tasks/SerialDateFields";
import { CompanyCombobox } from "./tasks/CompanyCombobox";
import { LinkTasksModal } from "./tasks/LinkTasksModal";
import { RecurringReminderFields } from "./tasks/RecurringReminderFields";
import { defaultRecurringValue, resolveRecurringReminder } from "../lib/reminderUtils";
import { showReminderToast } from "../lib/reminderToast";
import { confirmDialog, promptDialog, choiceDialog } from "../lib/confirm";
import { printTasks, exportTasksExcel, exportTasksWord } from "../lib/taskExport";
import ExportSelectModal from "./ExportSelectModal";
import { TaskPasteMenu } from "./tasks/TaskPasteMenu";
import { BackupHealthCard } from "./BackupHealthCard";
import { TemplateBar } from "./tasks/TemplateBar";
import { TemplatesModal } from "./tasks/TemplatesModal";
import { useTaskClipboard, clearTaskClipboard, setTaskClipboard, updateTaskClipboard } from "../lib/taskClipboard";

// Faz 9 CP6 — TasksPanel bileşenleri ayrı dosyalara taşındı (davranış birebir aynı).
import { TaskCard } from "./TaskCard";
import { useTaskSmartDelete } from "../lib/taskSmartDelete";
import { useTaskBulk } from "../lib/useTaskBulk";
import { useRenkGuncelleme } from "../lib/taskColors";
import { useEditQueue } from "../lib/useEditQueue";
import { useTaskMove } from "../lib/useTaskMove";
import { MoveTargetModal } from "./tasks/MoveTargetModal";
import { TaskBulkBar } from "./tasks/TaskBulkBar";
import { noAutofill } from "../lib/noAutofill";
import { useAutofillGuard } from "../lib/useAutofillGuard";
import {
  ReorderableTaskCard,
  TaskSortableGroup,  DetachedPlaceholderCard,  ReorderablePlaceholder,
  OuterTaskRow,
  GroupWindowMemberRow,
  StaticTaskGroupBlock,
  TaskGroupBlock,
  DetachedTaskWindow,
  DetachedGroupWindow,
  DetachedSubtaskWindow,
  GroupDetachedRow,
  placeDetachedAt,
} from "./TaskGroupViews";
import { NestDropDot } from "../lib/nestDrop";
import { DetachedSubtaskContent } from "./DetachedSubtaskContent";
import { DetachedTaskCardDnd } from "./DetachedTaskCardDnd";
const FILTER_ORDER_KEY = "sertex_task_filter_order_v1";

// Faz 9 CP5 — helpers moved to /lib/taskHelpers.js (ACTION_LOCK_MAP,
// LOCK_AUDIT_EVENT_META, formatAuditPayload, hasActiveUnlock, canManageLocks,
// isActionLocked, isOverdue, resolveThreshold, dueSoonLayer, statusStyle,
// playReminderBeep). Import block at the top of this file.
// LOCK_KEY_LABELS and LOCK_KEY_ORDER live in /lib/taskLocks.js.


const DETACHED_IDS_KEY = "sertex_detached_task_ids_v1";
const DETACHED_GROUP_IDS_KEY = "sertex_detached_group_ids_v1";
// AŞAMA 9 (Adım 2) — dışarı alınmış ALT GÖREV id'leri.
const DETACHED_SUB_IDS_KEY = "sertex_detached_subtask_ids_v1";
const useReminderScheduler = (tasks, onFire, onFireSub) => {
  useEffect(() => {
    // Request notification permission on first mount
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    const check = () => {
      const now = Date.now();
      tasks.forEach((t) => {
        if (t.archived) return;
        // Parent task reminder
        if (t.reminder_at && !t.reminder_fired && t.status !== "done") {
          const when = new Date(t.reminder_at).getTime();
          if (when <= now && when > now - 5 * 60 * 1000) {
            onFire(t);
          }
        }
        // Subtask due-date reminders (iç içe çocuklar dahil, id bazlı)
        const subs = flattenSubs(t.subtasks);
        subs.forEach((s) => {
          if (!s.due_date || s.reminder_fired) return;
          if (s.done || s.status === "done" || s.status === "paused") return;
          const when = new Date(s.due_date).getTime();
          if (when <= now && when > now - 5 * 60 * 1000) {
            onFireSub(t, s);
          }
        });
      });
    };
    check();
    const iv = setInterval(check, 20000);
    return () => clearInterval(iv);
  }, [tasks, onFire, onFireSub]);
};

// ============ MAIN PANEL ============
const ALL_FILTERS = ["aktif", "gecti", "bekliyor", "bitti"];
const DEFAULT_FILTER_ORDER = ["aktif", "gecti", "bekliyor", "bitti"];

// Draggable stat/filter button (long-press or grip)
const DraggableStat = ({ stat, active, onToggle }) => {
  const controls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);
  return (
    <Reorder.Item
      value={stat.key}
      dragListener={false}
      dragControls={controls}
      as="div"
      className="min-w-0"
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setTimeout(() => setIsDragging(false), 50)}
      whileDrag={{ scale: 1.08, zIndex: 50, boxShadow: "0 6px 24px rgba(0,240,255,0.5)" }}
      data-testid={`task-stat-wrap-${stat.key}`}
    >
      <div
        onClick={(e) => {
          if (isDragging) return;
          onToggle();
        }}
        onPointerDown={(e) => {
          const target = e.currentTarget;
          const startX = e.clientX;
          const startY = e.clientY;
          const timer = setTimeout(() => controls.start(e), 250);
          const cancel = (ev) => {
            if (ev && (Math.abs(ev.clientX - startX) > 4 || Math.abs(ev.clientY - startY) > 4)) {
              clearTimeout(timer);
              target.removeEventListener("pointermove", cancel);
              target.removeEventListener("pointerup", clear);
            }
          };
          const clear = () => {
            clearTimeout(timer);
            target.removeEventListener("pointermove", cancel);
            target.removeEventListener("pointerup", clear);
          };
          target.addEventListener("pointermove", cancel);
          target.addEventListener("pointerup", clear);
        }}
        data-testid={`task-stat-${stat.key}`}
        aria-pressed={active}
        title={`${stat.label} — sıralamak için basılı tutup sürükleyin`}
        style={{ cursor: "grab" }}
        className={`relative glass-panel border rounded-md px-1 py-1.5 transition-all select-none ${
          active
            ? `border-transparent ring-2 ${stat.ring} bg-sertex-cyan/10 scale-[1.03]`
            : "border-sertex-cyan/20 hover:border-sertex-cyan/40 hover:bg-sertex-cyan/5"
        }`}
      >
        <span
          onPointerDown={(e) => {
            e.stopPropagation();
            controls.start(e);
          }}
          className="absolute top-0.5 left-0.5 text-sertex-cyan/60 hover:text-sertex-cyan cursor-grab active:cursor-grabbing touch-none"
          data-testid={`task-stat-grip-${stat.key}`}
          title="Sürükleyerek sırala"
        >
          <GripVertical className="h-3 w-3" />
        </span>
        <div className={`display-text text-lg tabular-nums text-center ${stat.color}`}>{stat.value}</div>
        <div className="hud-text text-sertex-textMuted text-[9px] text-center">{stat.label}</div>
      </div>
    </Reorder.Item>
  );
};

const CAT_ORDER_KEY = "sertex_category_order_v1";
const loadCatOrder = () => {
  try { return JSON.parse(localStorage.getItem(CAT_ORDER_KEY) || "[]") || []; }
  catch { return []; }
};

// Görev → İş Kolu sürükle-bırak: bırakma noktasındaki (imleç) DOM yığınından
// ilk `data-cat-drop` taşıyan elemanı bulur. Framer Motion drag olayının
// clientX/clientY'sini (yoksa info.point) kullanır. `elementsFromPoint`
// (çoğul) sürüklenen kart imlecin üstünde olsa bile altındaki çipi yakalar.
const findCatDropTarget = (event, info) => {
  let x, y;
  if (event && typeof event.clientX === "number") { x = event.clientX; y = event.clientY; }
  else if (event?.changedTouches?.[0]) { x = event.changedTouches[0].clientX; y = event.changedTouches[0].clientY; }
  else if (info?.point) { x = info.point.x; y = info.point.y; }
  if (x == null || y == null) return null;
  const stack = (typeof document.elementsFromPoint === "function"
    ? document.elementsFromPoint(x, y)
    : [document.elementFromPoint(x, y)]) || [];
  for (const node of stack) {
    const c = node && node.closest && node.closest("[data-cat-drop]");
    if (c) return c.getAttribute("data-cat-drop");
  }
  return null;
};

const TasksPanel = ({ refreshSignal, onDataChanged, detached = false, initialCategory = null, onDock }) => {
  // FAZ 5 — iş kolu pencereleri sidebar'dan AYRI dnd kapsamıdır (pencereler arası "+İÇİNE").
  const dndKapsam = detached ? `panel:${initialCategory || "tum"}` : "sidebar";
  const { isTeamView, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  useRenkGuncelleme(setTasks);
  // GÖREV BAĞLAMA — gruplar (id → {name, show_progress}) + modal durumu.
  const [groups, setGroups] = useState([]);
  const [linkModal, setLinkModal] = useState(null); // { mode:'create'|'edit', taskId?, groupId? }
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  // Arşiv grupları: 'done' (BİTMİŞ) · 'cancelled' (İPTAL) · 'deleted' (SİLİNMİŞ/çöp).
  const [archiveGroup, setArchiveGroup] = useState("done");
  const [archiveCounts, setArchiveCounts] = useState({ done: 0, cancelled: 0, deleted: 0 });
  // Arşiv v2 — global ayarlar (neden politikası + otomatik temizlik) + kişi yetkileri.
  const [taskSettings, setTaskSettings] = useState({
    delete_reason_policy: "optional",
    trash_autoclean_enabled: false,
    trash_autoclean_days: 30,
    caps: { perm_delete: false, empty_trash: false, manage_policy: false },
  });
  // Genel aramada arşiv sonuçları (BİTMİŞ/İPTAL/SİLİNMİŞ) — ayrı bölümde gösterilir.
  const [archiveSearchResults, setArchiveSearchResults] = useState([]);
  // Arşiv sıralama yönü: 'new' (yeni→eski) | 'old' | 'az'
  const [archiveSort, setArchiveSort] = useState("new");
  // Arşivde iş koluna (kategori) göre gruplama — varsayılan kapalı (düz liste).
  const [archiveByCategory, setArchiveByCategory] = useState(false);
  // Gruplu arşivde iş kolu başlığına göre hızlı filtre.
  const [archiveCatQuery, setArchiveCatQuery] = useState("");
  // Her iş kolu grubunun İÇİNDE görev arama (grup açılınca): { [catKey]: query }
  const [archiveGroupQuery, setArchiveGroupQuery] = useState({});
  // Gruplu arşivde katlanmış (kapalı) iş kolu başlıkları.
  const [collapsedArchiveCats, setCollapsedArchiveCats] = useState(() => new Set());
  const toggleArchiveCat = (key) =>
    setCollapsedArchiveCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  const [showAddForm, setShowAddForm] = useState(false);
  // FAZ D — taslak alt görevler (imperative erişim: yazarken re-render olmasın)
  const subsRef = useRef(null);
  const [failedSubtasks, setFailedSubtasks] = useState(null);
  const createdIdRef = useRef(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [assigneeName, setAssigneeName] = useState("");
  const [companyName, setCompanyName] = useState("");
  // UX Enhancement — track whether companyName was auto-filled by the
  // assignee picker (so we can overwrite it when the pick changes) vs.
  // manually typed by the user (leave alone).
  const [companyAutoFilled, setCompanyAutoFilled] = useState(false);
  // Faz 8 CP3 — visible team members (used to populate the assignee dropdown
  // in Team-view mode). Empty for pure employees.
  const [teamMembers, setTeamMembers] = useState([]);
  const [assigneeUserIds, setAssigneeUserIds] = useState([]); // [] = assign to self
  // Yeni Görev formu — görev oluşunca yüklenecek bekleyen dosyalar.
  const [pendingFiles, setPendingFiles] = useState([]);
  // Faz 8 CP4 · İş Kolları — filter chip'leri + form dropdown + context menu.
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(initialCategory || ""); // "" = all, "__none__" = uncategorized
  const [newCategoryId, setNewCategoryId] = useState("");
  // Kalıcı Seri No + oluşturulma tarihi etiketi (Yeni Görev formu).
  const [newAssignSerial, setNewAssignSerial] = useState(false);
  const [newShowDate, setNewShowDate] = useState(false);
  // Görev arama — binlerce görev içinde başlık/açıklama/kişi/şirket/iş kolu/alt
  // görevlere göre canlı arama. Hiçbir mevcut filtreyi bozmaz (üstüne biner).
  const [searchQuery, setSearchQuery] = useState("");
  // AŞAMA 13 (Düzeltme 3) — kutudaki metin ANINDA görünür, süzme 200 ms
  // gecikir. Temizlemede (X / boşaltma) gecikme beklenmez.
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    if (!searchQuery) { setSearchTerm(""); return; }
    const h = setTimeout(() => setSearchTerm(searchQuery), 200);
    return () => clearTimeout(h);
  }, [searchQuery]);
  const taskSearchRef = useRef(null);
  useAutofillGuard(taskSearchRef, searchQuery);
  // İş kolu çiplerini sürükle-bırak ile sırala — kişisel (localStorage),
  // sekmeler/pencereler arası olayla senkron. Backend'e dokunmaz.
  const [catOrder, setCatOrder] = useState(loadCatOrder);
  // Görev → İş Kolu sürükle-bırakta imlecin o an üstünde olduğu çip.
  const [taskDragOverCatId, setTaskDragOverCatId] = useState(null);
  // Görev Kopyalama (Kopyala → Yapıştır).
  const [pasteMenu, setPasteMenu] = useState(null); // { x, y, categoryId, categoryName }
  // AŞAMA 12 (Adım 2) — iş kolu sağ tık menüsü + pencereye ayrılmış kollar.
  const [catMenu, setCatMenu] = useState(null); // { x, y, id, name }
  const [detachedPanelList, setDetachedPanelList] = useState(getDetachedPanels);
  // FAZ C — otomatik pencere düzeni geri yükleme tercihi (varsayılan KAPALI).
  const [layoutAutoRestore, setLayoutAutoRestore] = useState(false);
  useEffect(() => subscribeDetachedPanels(setDetachedPanelList), []);
  const detachedCatPanels = useMemo(() => {    const m = new Map();
    for (const p of detachedPanelList) if (p.category) m.set(p.category, p.id);
    return m;
  }, [detachedPanelList]);
  const clipboard = useTaskClipboard();
  // Görev Şablonları (Şablon Kütüphanesi).
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templatesRefresh, setTemplatesRefresh] = useState(0);
  const orderedCategories = useMemo(() => {
    if (!catOrder.length) return categories;
    const pos = new Map(catOrder.map((id, i) => [id, i]));
    return [...categories].sort((a, b) => {
      const pa = pos.has(a.id) ? pos.get(a.id) : Infinity;
      const pb = pos.has(b.id) ? pos.get(b.id) : Infinity;
      if (pa === pb) return 0;
      return pa - pb;
    });
  }, [categories, catOrder]);
  useEffect(() => {
    const h = () => setCatOrder(loadCatOrder());
    window.addEventListener("sertex:category-order", h);
    return () => window.removeEventListener("sertex:category-order", h);
  }, []);
  // İş kolu sırasını sunucudan çek (cihazlar arası senkron). localStorage
  // anlık boyar; sunucu değeri gelince onu esas al ve cache'i güncelle.
  useEffect(() => {
    taskCategoriesApi.getOrder().then((d) => {
      const order = Array.isArray(d?.order) ? d.order : [];
      if (order.length) {
        setCatOrder(order);
        try { localStorage.setItem(CAT_ORDER_KEY, JSON.stringify(order)); } catch (e) { /* ignore */ }
      }
    }).catch(() => {});
  }, [refreshSignal]);
  const persistCatOrder = (ids) => {
    setCatOrder(ids);
    try { localStorage.setItem(CAT_ORDER_KEY, JSON.stringify(ids)); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event("sertex:category-order"));
    // Sunucuya kaydet (localStorage zaten güncel; hata olursa kullanıcıyı uyar).
    taskCategoriesApi.setOrder(ids).catch(() =>
      toast.error("İş kolu sırası sunucuya kaydedilemedi — diğer cihazlarla eşitlenmeyebilir")
    );
  };
  // Görev sürüklenirken hedef çipi vurgula (imleç üstündeki çip).
  const handleTaskDragToCategory = (event, info) => {
    const target = findCatDropTarget(event, info);
    setTaskDragOverCatId((prev) => (prev === target ? prev : target));
  };
  // Görev bir iş kolu çipine bırakıldı → o iş koluna taşı (KOLSUZ → kaldır).
  const handleTaskDropCategory = (task, event, info) => {
    const target = findCatDropTarget(event, info);
    setTaskDragOverCatId(null);
    if (!target || !task) return;
    const current = task.category_id || "";
    const next = target === "__none__" ? "" : target;
    if (current === next) return;
    setTaskCategory(task.id, next);
  };
  // Faz 8 CP5 — Due-soon reminder overrides on the new-task form.
  // reminderDays: null = varsayılan (hiyerarşi); sayı = özel; -1 = kapalı.
  const [newReminderDays, setNewReminderDays] = useState(null);
  const [newReminderDisabled, setNewReminderDisabled] = useState(false);
  // Tekrarlı hatırlatıcı — oluşturma formunda ayarlanabilir ("her yerde olsun").
  const [newReminder, setNewReminder] = useState(defaultRecurringValue());
  // Config from /api/settings/reminder-config — used for visual layer + form hint.
  const [reminderConfig, setReminderConfig] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editTaslakOto, setEditTaslakOto] = useState(false);
  const [tick, setTick] = useState(0);
  // Görevler sekmesi: "mine" (Benim Görevlerim) | "team" (Personel Görevleri).
  const [taskScope, setTaskScope] = useState("mine");
  // Team scope — which person's tasks to show ("" = Tümü).
  const [personFilter, setPersonFilter] = useState("");
  // Görev kartı Küçült/Büyüt — collapsed görev id'leri (localStorage'da hatırlanır).
  const COLLAPSE_KEY = "sertex_collapsed_task_ids";
  // Dışa aktarmada saat gösterimi tercihi ("Saatleri de göster").
  const [exportShowTime, setExportShowTime] = useState(true);
  useEffect(() => {
    let alive = true;
    subtaskDatePrefsApi.get()
      .then((p) => { if (alive && p?.export_show_time !== undefined) setExportShowTime(!!p.export_show_time); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const [collapsedIds, setCollapsedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const persistCollapsed = (set) => {
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify(Array.from(set)));
    } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
  };
  const toggleCollapse = (id) => {
    setCollapsedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistCollapsed(next);
      return next;
    });
  };
  const setAllCollapsed = (ids, collapsed) => {
    setCollapsedIds((s) => {
      const next = new Set(s);
      ids.forEach((id) => (collapsed ? next.add(id) : next.delete(id)));
      persistCollapsed(next);
      return next;
    });
  };
  // Task 4 — dışarı alınmış (detached) görev id'leri (yüzen pencerede açık).
  // Kalıcı değil; sayfa yenilenince pencereler kapanır, kartlar listeye döner.
  const [detachedIds, setDetachedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(DETACHED_IDS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const toggleDetach = (id) => {
    setDetachedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try { localStorage.setItem(DETACHED_IDS_KEY, JSON.stringify(Array.from(next))); } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
      return next;
    });
  };
  // AŞAMA 8 — Kart panelin dışına SÜRÜKLENEREK bırakıldı: bırakıldığı
  // koordinata yerleştirilip dışa taşınır. Listede yer tutucu (placeholder)
  // kaldığı için geri bırakıldığında ESKİ indeksine oturur.
  const detachByDrag = (id, pt) => {
    if (!id || detachedIds.has(id)) return;
    placeDetachedAt(id, pt.x, pt.y);
    toggleDetach(id);
  };
  // AŞAMA 9 (Adım 2) — dışarı alınmış (yüzen pencerede açık) alt görevler.
  const [detachedSubIds, setDetachedSubIds] = useState(() => {
    try {
      const raw = localStorage.getItem(DETACHED_SUB_IDS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  // AŞAMA 10 — YENİ KURAL: bir alt görev, ebeveyni dışarıda olsa bile KENDİ
  // penceresine çıkabilir. Ebeveynin penceresinde/listede boş iz kalır.
  // (Eski "ebeveyn ayrılınca torunların penceresi kapanır" davranışı kaldırıldı.)
  const toggleSubDetach = (subId) => {
    setDetachedSubIds((s) => {
      const next = new Set(s);
      if (next.has(subId)) next.delete(subId);
      else next.add(subId);
      try { localStorage.setItem(DETACHED_SUB_IDS_KEY, JSON.stringify(Array.from(next))); } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
      return next;
    });
  };
  // Hayalet pencere önlemi: üst görev silindi/arşivlendi ya da alt görev artık
  // yok ise dışarı alınmış kaydı temizlenir.
  useEffect(() => {
    if (detachedSubIds.size === 0 || tasks.length === 0 || showArchived) return;
    const gecerli = Array.from(detachedSubIds).filter((sid) =>
      tasks.some((t) => !t.archived && !t.cancelled && !t.deleted && findSubById(t.subtasks || [], sid))
    );
    if (gecerli.length === detachedSubIds.size) return;
    const next = new Set(gecerli);
    setDetachedSubIds(next);
    try { localStorage.setItem(DETACHED_SUB_IDS_KEY, JSON.stringify(gecerli)); } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
  }, [tasks, detachedSubIds]);
  // Alt görev dışarı alma — TEK fonksiyon: sürükleme yolu bırakma noktasını
  // (pt) verir, menü yolu pt vermez (pencere kendi varsayılan konumunda açılır).
  const detachSubByDrag = (subId, pt) => {
    if (!subId || detachedSubIds.has(subId)) return;
    if (pt && pt.x != null) placeDetachedAt(`sub:${subId}`, pt.x, pt.y);
    toggleSubDetach(subId);
  };
  // Alt görev sürüklemesinin başındaki hal (iptal edilirse geri dönülür).
  const subSnapRef = useRef(null);
  const subPreviewedRef = useRef(false);
  // AŞAMA 9 (Adım 1) — Alt görev sıralaması. Sürükleme görev listesinin TEK
  // dnd-kit sağlayıcısı üzerinden geldiği için burada ele alınır. Yalnızca
  // AYNI ebeveynin çocukları arasında yer değiştirir; başka bir ebeveynin
  // grubuna bırakma girişimi güvenle iptal edilir (veri bozulmaz).
  const handleSubtaskDrag = (event, localOnly, canceled) => {
    const src = event?.operation?.source;
    const { taskId, parentId } = src?.data || {};
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const all = Array.isArray(task.subtasks) ? task.subtasks : [];
    // Sürüklemenin BAŞINDAKİ hal — iptal edilirse buraya dönülür.
    if (localOnly && !subSnapRef.current) subSnapRef.current = { taskId, subs: all };
    let onizlendi = subPreviewedRef.current;
    if (!localOnly) {
      const snap = subSnapRef.current;
      subSnapRef.current = null;
      subPreviewedRef.current = false;
      if (canceled) {
        if (onizlendi && snap && snap.taskId === taskId) setSubtasks(taskId, snap.subs, { localOnly: true });
        return;
      }
    }
    const tgt = event?.operation?.target;
    if (!src || !tgt || src.type !== tgt.type) {
      // Başka ebeveynin grubuna bırakma → seviye değişmez. Ekrandaki sıra
      // (önizleme) yine de kalıcı yazılır, aksi halde sunucuyla uyumsuz kalır.
      if (!localOnly && onizlendi) setSubtasks(taskId, all, {});
      return;
    }
    const sibs = parentId == null ? all : (findSubById(all, parentId)?.children || []);
    const ids = sibs.map((s) => s.id);
    const nextIds = move(ids, event);
    const degisti = nextIds.length === ids.length && nextIds.some((id, i) => id !== ids[i]);
    if (!degisti) {
      // Sürükleme sırasında (onDragOver) önizleme uygulanmış olabilir;
      // bırakıldığında ekrandaki sırayı TEK istekle kalıcı yaz. Hiç önizleme
      // olmadıysa (gerçek bir değişiklik yok) gereksiz istek atılmaz.
      if (!localOnly && onizlendi) setSubtasks(taskId, all, {});
      return;
    }
    const byId = Object.fromEntries(sibs.map((s) => [s.id, s]));
    const nextSibs = nextIds.map((id) => byId[id]).filter(Boolean);
    if (nextSibs.length !== sibs.length) return;
    if (localOnly) subPreviewedRef.current = true;
    setSubtasks(taskId, replaceChildrenById(all, parentId, nextSibs), localOnly ? { localOnly: true } : {});
  };
  const [detachedGroupIds, setDetachedGroupIds] = useState(() => {
    try {
      const raw = localStorage.getItem(DETACHED_GROUP_IDS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const toggleGroupDetach = (gid) => {
    setDetachedGroupIds((s) => {
      const next = new Set(s);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      try { localStorage.setItem(DETACHED_GROUP_IDS_KEY, JSON.stringify(Array.from(next))); } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
      return next;
    });
  };
  // Multi-select filter: empty = show all, otherwise show only selected buckets
  const [filters, setFilters] = useState([]); // array of 'aktif'|'gecti'|'bekliyor'|'bitti'

  const [filterOrder, setFilterOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(FILTER_ORDER_KEY);
      if (!raw) return DEFAULT_FILTER_ORDER;
      const parsed = JSON.parse(raw);
      if (
        Array.isArray(parsed) &&
        parsed.length === DEFAULT_FILTER_ORDER.length &&
        DEFAULT_FILTER_ORDER.every((k) => parsed.includes(k))
      ) {
        return parsed;
      }
    } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
    return DEFAULT_FILTER_ORDER;
  });

  const persistFilterOrder = (next) => {
    setFilterOrder(next);
    try {
      localStorage.setItem(FILTER_ORDER_KEY, JSON.stringify(next));
    } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
  };

  const toggleFilter = (key) => {
    setFilters((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const clearFilters = () => setFilters([]);

  const caps = taskSettings.caps || {};
  const ARCHIVE_VIEW = { done: "archived", cancelled: "cancelled", deleted: "trash" };

  const load = useCallback(() => {
    if (showArchived) {
      tasksApi.list(true, taskScope, ARCHIVE_VIEW[archiveGroup] || "archived").then(setTasks).catch(() => {});
      tasksApi.archiveCounts(taskScope).then(setArchiveCounts).catch(() => {});
    } else {
      tasksApi.list(false, taskScope).then(setTasks).catch(() => {});
    }
    // GÖREV BAĞLAMA — grup meta'sını (isim/ilerleme bayrağı) yükle.
    tasksApi.listGroups().then(setGroups).catch(() => setGroups([]));
    // Also refresh archived count (lightweight, only for badge) — always for
    // the personal scope so the badge reflects the user's own archive.
    if (!showArchived) {
      tasksApi.archiveCounts(taskScope)
        .then((c) => { setArchiveCounts(c); setArchivedCount((c.done || 0) + (c.cancelled || 0) + (c.deleted || 0)); })
        .catch(() => {});
    }
  }, [showArchived, taskScope, archiveGroup]);

  // Panel örnekleri arası CANLI SENKRON: her başarılı görev değişikliğinden
  // sonra çağrılır → hem üst bileşene (stat rozetleri/refreshKey) haber verir
  // hem de GLOBAL bir olay yayınlar; böylece aynı anda açık olan TÜM TasksPanel
  // örnekleri (sağdaki Neural Link + dışarı taşınan pencereler) listelerini
  // anında tazeler. load() olayı yaymadığı için döngü oluşmaz.
  const notifyTasksChanged = useCallback(() => {
    if (onDataChanged) onDataChanged();
    try { window.dispatchEvent(new Event("sertex:tasks-changed")); } catch (e) { /* ignore */ }
  }, [onDataChanged]);

  // AŞAMA 11 — akıllı silme diyaloğu (alt görevli görev) sonrası da o göreve
  // ait yüzen pencereler kapatılsın diye silinen görevin id'si tutulur.
  const smartDeleteIdRef = useRef(null);
  const smartDelete = useTaskSmartDelete({
    refresh: () => {
      if (smartDeleteIdRef.current) {
        closeWindowsForTask(smartDeleteIdRef.current);
        smartDeleteIdRef.current = null;
      }
      load();
      notifyTasksChanged();
    },
  });

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  // Başka bir panel örneği (ör. dışarı taşınan pencere) görev değiştirince
  // bu örnek de kendini tazeler.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener("sertex:tasks-changed", onChanged);
    return () => window.removeEventListener("sertex:tasks-changed", onChanged);
  }, [load]);

  // Toast hızlı aksiyonu (ertele/tamamla) sonrası listeyi tazele.
  useEffect(() => {
    const onAction = () => load();
    window.addEventListener("sertex:reminder-action", onAction);
    return () => window.removeEventListener("sertex:reminder-action", onAction);
  }, [load]);

  // When switching to the team scope we need team members for the person
  // filter chips; fetch them (silent-fail for pure employees).
  useEffect(() => {
    if (taskScope === "team" && isTeamView) {
      teamApi.members().then(setTeamMembers).catch(() => setTeamMembers([]));
      setPersonFilter("");
    }
  }, [taskScope, isTeamView]);

  // Faz 8 CP3 — fetch team members visible to the caller. Silent-fail on
  // employees/unauthenticated (backend returns []). Only refetch when the
  // add-form opens so we surface any freshly-granted visibility rows.
  useEffect(() => {
    if (!showAddForm) return;
    teamApi.members().then(setTeamMembers).catch(() => setTeamMembers([]));
  }, [showAddForm]);

  // Görevi Düzenle modalı açılınca ekip üyelerini yükle — böylece GÖREV SAHİBİ
  // ve ŞİRKET alanları (şirkete bağlı personel listesi) dropdown olarak gelir.
  // "Benim Görevlerim"den düzenlerken teamMembers boş kalmasın diye gerekli.
  useEffect(() => {
    if (editing && isTeamView) {
      teamApi.members().then(setTeamMembers).catch(() => {});
    }
  }, [editing, isTeamView]);

  // Faz 8 CP4 — load categories once (silent-fail on 401/employee-no-company).
  useEffect(() => {
    taskCategoriesApi.list("my_tasks").then(setCategories).catch(() => setCategories([]));
  }, [refreshSignal]);

  // AŞAMA 12 (Adım 2) — TEMİZLİK DETERMİNİZMİ: silinmiş / artık görünmeyen bir
  // iş koluna ait açık pencereler anında kapanır (alt kollar da silindiği için
  // onların pencereleri de aynı kontrolden geçer). Yalnızca ana panel örneği
  // çalıştırır; yüzen pencereler kendi kendini kapatmaya çalışmaz.
  useEffect(() => {
    if (detached || !categories.length) return;
    const gecerli = new Set(categories.map((c) => c.id));
    for (const [catId, panelId] of detachedCatPanels.entries()) {
      if (!gecerli.has(catId)) closeDetachedPanel(panelId);
    }
  }, [categories, detachedCatPanels, detached]);

  // Faz 8 CP5 — load reminder config (drives the visual due-soon layer and the
  // "Şirket varsayılanı" hint in the new-task form + settings). Refreshed on
  // parent data-signal so a settings change is reflected without a hard reload.
  useEffect(() => {
    reminderConfigApi.get().then(setReminderConfig).catch(() => setReminderConfig(null));
  }, [refreshSignal]);

  // Team Faz 2 · notification bell → task jump. Store the jumped-to id so the
  // TaskCard can render a temporary highlight ring + scrollIntoView after the
  // task list re-renders. The highlight auto-clears after 4 s so it doesn't
  // sticks around forever if the user is idle.
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);

  // Faz 9 CP4.35 — track active reminder beeps so we can cancel their queued
  // 2nd-beep + AudioContext close timeouts when the panel unmounts.
  const activeBeepsRef = useRef(new Set());
  useEffect(() => () => {
    // Copy to avoid mutation during iteration.
    const beeps = Array.from(activeBeepsRef.current);
    activeBeepsRef.current.clear();
    beeps.forEach((h) => { try { h.cancel(); } catch { /* ok */ } });
  }, []);
  const beep = useCallback(() => {
    const handle = playReminderBeep();
    activeBeepsRef.current.add(handle);
    // Auto-remove from set after natural completion so the set doesn't grow.
    setTimeout(() => activeBeepsRef.current.delete(handle), 1400);
    return handle;
  }, []);
  useEffect(() => {
    // FAZ B — hedef görev süzülmüş bir görünümde gizli kalmasın: atlama isteği
    // `clear_filters` ile gelirse aktif süzgeçler temizlenir.
    const clearFilters = () => {
      setSearchQuery("");
      setSearchTerm("");
      setFilters([]);
      setCategoryFilter("");
      setPersonFilter("");
      setShowArchived(false);
    };
    const doJump = (tid) => {
      if (!tid) return;
      setHighlightedTaskId(tid);
      // Wait for React to render, then scroll and pulse.
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="task-item-${tid}"]`);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // FAZ B — uzun sayfalarda iç kapsayıcı kaydırılsa bile kart pencere
          // görünümünün dışında kalabiliyordu; sayfayı da hizala.
          setTimeout(() => {
            const r = el.getBoundingClientRect();
            if (r.top < 0 || r.bottom > window.innerHeight) {
              window.scrollBy({
                top: r.top - window.innerHeight / 2 + r.height / 2,
                behavior: "smooth",
              });
            }
          }, 350);
        }
      }, 250);
      // Auto-clear so the pulse doesn't stay indefinitely.
      setTimeout(() => setHighlightedTaskId((cur) => (cur === tid ? null : cur)), 4000);
    };
    // 1) Handle any pending jump that was dispatched BEFORE this panel
    //    mounted (e.g., a notification click switched to the Görevler tab
    //    and thus mounted us for the first time).
    const pending = window.__sertex_pending_task_jump;
    if (pending && Date.now() - pending.ts < 5000) {
      delete window.__sertex_pending_task_jump;
      // Refresh once so a task that just became visible is in state.
      load();
      doJump(pending.task_id);
    }
    // 2) Also listen live for subsequent jumps while we're mounted.
    const onJump = (ev) => {
      const tid = ev?.detail?.task_id;
      if (!tid) return;
      delete window.__sertex_pending_task_jump;
      if (ev?.detail?.clear_filters) clearFilters();
      load();
      doJump(tid);
    };
    window.addEventListener("sertex:task-jump", onJump);
    return () => window.removeEventListener("sertex:task-jump", onJump);
  }, [load]);

  // Faz 9 CP4.33 — Global unlock request from NotificationBell.
  // Clicking a `task_unlock_offered` notification fires a
  // `sertex:task-unlock-request` CustomEvent. We refetch the task, then
  // open the panel-scoped UnlockOtpModal without navigating first — quick
  // path (2 clicks: bell → row → done).
  //
  // Faz 9 CP4.34 — race-safe. Previous listener depended on outer `load`
  // (which changed identity across renders) — the effect captured a stale
  // closure so late unlock events would refetch based on old filter state.
  // Now the listener only depends on the stable `tasksApi.get` API and
  // `mounted` guard prevents state updates after unmount.
  const [pendingUnlockTask, setPendingUnlockTask] = useState(null);
  useEffect(() => {
    let mounted = true;
    const onUnlock = async (ev) => {
      const tid = ev?.detail?.task_id;
      if (!tid) return;
      try {
        // Fetch the task in isolation — no need to refresh the whole list
        // before opening the modal. The modal itself calls `onLockChanged`
        // (which triggers `load()`) after the OTP is verified.
        const t = await tasksApi.get(tid);
        if (mounted && t) setPendingUnlockTask(t);
        else if (mounted) toast.error("Görev bulunamadı — belki silindi ya da izin yok");
      } catch {
        if (mounted) toast.error("Görev yüklenemedi");
      }
    };
    window.addEventListener("sertex:task-unlock-request", onUnlock);
    return () => {
      mounted = false;
      window.removeEventListener("sertex:task-unlock-request", onUnlock);
    };
  }, []);

  // Arşiv ayarlarını yükle (neden politikası + otomatik temizlik + kişi yetkileri).
  useEffect(() => {
    let alive = true;
    tasksApi.getSettings().then((s) => { if (alive) setTaskSettings(s); }).catch(() => {});
    return () => { alive = false; };
  }, [showArchived]);

  // Genel arama arşivi de tarasın — aktif görünümde arama yapılınca arşiv
  // (BİTMİŞ/İPTAL/SİLİNMİŞ) eşleşmelerini ayrı bölümde göstermek için debounce'lu getir.
  useEffect(() => {
    const q = searchTerm.trim();
    if (showArchived || q.length < 2) { setArchiveSearchResults([]); return; }
    const h = setTimeout(() => {
      tasksApi.searchArchive(q, taskScope).then(setArchiveSearchResults).catch(() => setArchiveSearchResults([]));
    }, 300);
    return () => clearTimeout(h);
  }, [searchTerm, showArchived, taskScope]);

  // Neden notu iste — politikaya göre. Döner: {ok, reason}. İptal → {ok:false}.
  const askReason = async (verb) => {
    const policy = taskSettings.delete_reason_policy;
    if (policy === "off") return { ok: true, reason: "" };
    // "required" ise boş bırakılamaz — geçerli girene kadar tekrar sor.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const r = await promptDialog({
        title: verb === "cancel" ? "GÖREVİ İPTAL ET" : "GÖREVİ SİL",
        message: policy === "required"
          ? `Bu işlem için bir neden girmeniz zorunlu.`
          : `İsterseniz kısa bir neden ekleyin (boş bırakabilirsiniz).`,
        placeholder: "Neden (kısa not)...",
        confirmText: verb === "cancel" ? "İPTAL ET" : "SİL",
        cancelText: "VAZGEÇ",
        danger: true,
      });
      if (r === null) return { ok: false };
      const reason = (r || "").trim();
      if (policy === "required" && !reason) {
        toast.error("Bu işlem için neden zorunlu");
        continue;
      }
      return { ok: true, reason };
    }
  };

  // AŞAMA 11 — Görev arşivlenir/iptal edilir/silinirse o ağaca ait TÜM
  // pencereler (görev penceresi + alt görev pencereleri) kapatılır ve
  // localStorage kalıntısı temizlenir. Aksiyon anında çalışır (deterministik).
  const closeWindowsForTask = (id) => {
    const t = tasks.find((x) => x.id === id);
    const subIds = t ? new Set(flattenSubs(t.subtasks || []).map((s) => s.id)) : new Set();
    setDetachedIds((s) => {
      if (!s.has(id)) return s;
      const next = new Set(s);
      next.delete(id);
      try { localStorage.setItem(DETACHED_IDS_KEY, JSON.stringify(Array.from(next))); } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
      return next;
    });
    if (subIds.size === 0) return;
    setDetachedSubIds((s) => {
      const next = new Set(Array.from(s).filter((sid) => !subIds.has(sid)));
      if (next.size === s.size) return s;
      try { localStorage.setItem(DETACHED_SUB_IDS_KEY, JSON.stringify(Array.from(next))); } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
      return next;
    });
  };

  const setArchived = async (id, archived) => {
    if (archived) closeWindowsForTask(id);
    // Optimistic remove/keep depending on view
    setTasks((prev) => prev.filter((t) => t.id !== id));
    setArchivedCount((c) => (archived ? c + 1 : Math.max(0, c - 1)));
    // Faz 9 CP8.4 — show the confirmation toast IMMEDIATELY after the
    // optimistic UI update (the user already sees the row leave). Waiting
    // for the network round-trip made the toast feel very laggy on mobile
    // (2-3s delay on slow connections). If the backend rejects the change
    // we roll back with an error toast + full reload.
    toast.success(archived ? "Görev arşivlendi" : "Arşivden çıkarıldı");
    try {
      await tasksApi.setArchived(id, archived);
      notifyTasksChanged();
    } catch (e) {
      toast.error("İşlem başarısız — geri alınıyor");
      load();
    }
  };

  // İptal Et — görevi "iptal edilmiş" işaretle + arşive taşı (İPTAL grubu).
  const cancelTask = async (id) => {
    const res = await askReason("cancel");
    if (!res.ok) return;
    closeWindowsForTask(id);
    setTasks((prev) => prev.filter((t) => t.id !== id));
    toast.success("Görev iptal edildi");
    try {
      await tasksApi.cancel(id, res.reason);
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error(e?.response?.status === 423 ? (e.response.data?.detail || "Görev kilitli") : "İptal başarısız");
      load();
    }
  };

  // İptali geri al — görevi aktif listeye döndür.
  const uncancelTask = async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    toast.success("Görev geri yüklendi");
    try {
      await tasksApi.uncancel(id);
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error("Geri yükleme başarısız");
      load();
    }
  };

  // Çöp kutusundan geri yükle.
  const restoreTask = async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    toast.success("Görev geri yüklendi");
    try {
      await tasksApi.restore(id);
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error("Geri yükleme başarısız");
      load();
    }
  };

  // ================= FAZ C — PENCERE DÜZENİ HAFIZASI =================
  // Mevcut yüzen pencerelerin anlık görüntüsü. Geometri, pencerelerin kendi
  // localStorage deposundan okunur (react-rnd mantığına dokunulmaz).
  const collectLayoutWindows = useCallback(() => {
    const vp = currentViewport();
    const windows = [];
    Array.from(detachedIds).forEach((id) => {
      const g = readTaskGeom("task", id) || {};
      windows.push({
        kind: "task",
        item_id: id,
        title: tasks.find((t) => t.id === id)?.title || null,
        x: g.x ?? 0, y: g.y ?? 0,
        width: g.width ?? 640, height: g.height ?? 680,
        z: 62,
      });
    });
    Array.from(detachedSubIds).forEach((sid) => {
      const g = readTaskGeom("subtask", sid) || {};
      let baslik = null;
      for (const t of tasks) {
        const s = findSubById(t.subtasks || [], sid);
        if (s) { baslik = s.title; break; }
      }
      windows.push({
        kind: "subtask",
        item_id: sid,
        title: baslik,
        x: g.x ?? 0, y: g.y ?? 0,
        width: g.width ?? 520, height: g.height ?? 420,
        z: 62,
      });
    });
    detachedPanelList.filter((p) => p.category).forEach((p) => {
      const g = readPanelGeom(p.id) || {};
      windows.push({
        kind: "category",
        item_id: p.category,
        title: p.categoryName || null,
        x: g.x ?? 140, y: g.y ?? 90,
        width: g.width ?? 460, height: g.height ?? 620,
        z: 62,
      });
    });
    // z-sırası: ekrandaki gerçek üstünlük sırası (küçük → altta).
    windows.forEach((w, i) => { w.z = 62 + i; });
    return { viewport: vp, windows };
  }, [detachedIds, detachedSubIds, detachedPanelList, tasks]);

  const openWindowCount =
    detachedIds.size + detachedSubIds.size + detachedPanelList.filter((p) => p.category).length;

  // Düzeni uygula. Sıra önemlidir: (1) onay, (2) tüm pencereleri kapat,
  // (3) geometriyi yaz, (4) state'leri TEK dalgada aç.
  const applyLayout = useCallback(async (layout, sessiz = false) => {
    const acik = openWindowCount > 0;
    if (acik && !sessiz) {
      const ok = await confirmDialog({
        title: "Düzeni Uygula",
        message: "Açık pencereler kapatılsın mı? Kayıtlı düzen bunun yerine açılacak.",
        confirmText: "Kapat ve Uygula",
      });
      if (!ok) return;
    }
    const vp = currentViewport();
    const savedVp = layout.viewport || vp;
    const gorevler = [];
    const altlar = [];
    const kollar = [];
    let eksik = 0;

    for (const w of layout.windows || []) {
      const g = fitGeom(w, savedVp, vp);
      if (w.kind === "task") {
        const t = tasks.find(
          (x) => x.id === w.item_id && !x.archived && !x.cancelled && !x.deleted,
        );
        if (!t) { eksik += 1; continue; }
        writeTaskGeom("task", w.item_id, g);
        gorevler.push(w.item_id);
      } else if (w.kind === "subtask") {
        const bulundu = tasks.some(
          (t) => !t.archived && !t.cancelled && !t.deleted && findSubById(t.subtasks || [], w.item_id),
        );
        if (!bulundu) { eksik += 1; continue; }
        writeTaskGeom("subtask", w.item_id, g);
        altlar.push(w.item_id);
      } else if (w.kind === "category") {
        const c = categories.find((x) => x.id === w.item_id);
        if (!c) { eksik += 1; continue; }
        kollar.push({ category: c.id, categoryName: c.name, geom: g });
      }
    }

    // Tek dalga: önce hepsini kapat, sonra hepsini aç (6 ayrı render dalgası
    // oluşmaz; her tip için TEK setState).
    closeAllDetachedPanels();
    setDetachedIds(new Set(gorevler));
    setDetachedSubIds(new Set(altlar));
    try {
      localStorage.setItem(DETACHED_IDS_KEY, JSON.stringify(gorevler));
      localStorage.setItem(DETACHED_SUB_IDS_KEY, JSON.stringify(altlar));
    } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
    if (kollar.length) openDetachedPanelsBatch(kollar);

    // Kaydedilen z-sırasını pencerelere ilet (mount sonrası).
    const zMap = {};
    for (const w of layout.windows || []) zMap[`${w.kind}:${w.item_id}`] = w.z || 62;
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("sertex:layout-z", { detail: zMap }));
    }, 120);

    try { await layoutsApi.setPrefs({ auto_restore: layoutAutoRestore, last_layout_id: layout.id }); } catch { /* sessiz */ }

    if (eksik > 0) {
      toast(`${eksik} öğe artık mevcut değil`, {
        description: "Kalan pencereler açıldı.",
        duration: 6000,
      });
    } else {
      toast.success(`"${layout.name}" düzeni uygulandı`);
    }
  }, [openWindowCount, tasks, categories, layoutAutoRestore]);

  // Açılışta otomatik geri yükleme (VARSAYILAN KAPALI). Açıksa en son
  // kullanılan düzen sessizce uygulanır — açılışta pencere yok, onay sorulmaz.
  const autoRestoreDoneRef = useRef(false);
  useEffect(() => {
    if (detached || autoRestoreDoneRef.current) return;
    if (!tasks.length) return;
    autoRestoreDoneRef.current = true;
    (async () => {
      try {
        const prefs = await layoutsApi.getPrefs();
        setLayoutAutoRestore(!!prefs.auto_restore);
        if (!prefs.auto_restore) return;
        const { layouts } = await layoutsApi.list();
        const hedef =
          (layouts || []).find((l) => l.id === prefs.last_layout_id) ||
          (layouts || [])[(layouts || []).length - 1];
        if (hedef) await applyLayout(hedef, true);
      } catch { /* sessiz */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.length, detached]);
  // "HEPSİNİ TOPLA" — tüm görev/alt görev/iş kolu pencerelerini TEK dalgada
  // panele geri takar; geri alma için mevcut düzen anlık görüntüsünü döner.
  const dockAllWindows = useCallback(() => {
    // Hiç sürüklenmemiş pencerelerin geometri kaydı yoktur; geri alındığında
    // sol üste kaymamaları için EKRANDAKİ gerçek kutuları bir kez kaydet.
    const kutu = (el) => {
      const r = el.getBoundingClientRect();
      return { x: Math.round(r.x), y: Math.round(r.y), width: Math.round(r.width), height: Math.round(r.height) };
    };
    document.querySelectorAll("[data-detached-taskwindow]").forEach((el) => {
      const id = el.getAttribute("data-detached-taskwindow");
      if (id && !readTaskGeom("task", id)) writeTaskGeom("task", id, kutu(el));
    });
    document.querySelectorAll("[data-detached-subwindow]").forEach((el) => {
      const id = el.getAttribute("data-detached-subwindow");
      if (id && !readTaskGeom("subtask", id)) writeTaskGeom("subtask", id, kutu(el));
    });
    getDetachedPanels().forEach((p) => {
      if (readPanelGeom(p.id)) return;
      const el = document.querySelector(`[data-testid="detached-panel-window-${p.id}"]`);
      if (el) writePanelGeom(p.id, kutu(el));
    });
    const snap = collectLayoutWindows();
    if (!snap.windows.length) return { sayi: 0, layout: null };
    closeAllDetachedPanels();
    setDetachedIds(new Set());
    setDetachedSubIds(new Set());
    try {
      localStorage.setItem(DETACHED_IDS_KEY, "[]");
      localStorage.setItem(DETACHED_SUB_IDS_KEY, "[]");
    } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
    return { sayi: snap.windows.length, layout: { name: "önceki yerleşim", ...snap } };
  }, [collectLayoutWindows]);

  useEffect(() => {
    if (detached) return;
    return registerWindowCollector("tasks-panel", {
      ana: true,
      say: () => openWindowCount,
      kapat: dockAllWindows,
      geriAc: (s) => (s?.layout ? applyLayout(s.layout, true) : undefined),
    });
  }, [detached, openWindowCount, dockAllWindows, applyLayout]);

  // FAZ F — düzen mantığını diğer arayüzlerin üst barına köprüle (yalnız ANA panel).
  useEffect(() => {
    if (detached) return;
    return registerLayoutBridge({
      collect: collectLayoutWindows,
      apply: applyLayout,
      count: openWindowCount,
    });
  }, [detached, collectLayoutWindows, applyLayout, openWindowCount]);
  // ================= /FAZ C =================


  // Kalıcı Sil (yalnızca admin) — çöp kutusundaki görevi geri dönüşsüz sil.
  const permanentDeleteTask = async (id) => {
    const ok = await confirmDialog({
      title: "Kalıcı Sil",
      message: "Bu görev geri dönüşü olmayacak şekilde kalıcı olarak silinecek. Emin misiniz?",
      confirmText: "Kalıcı Sil",
      danger: true,
    });
    if (!ok) return;
    setTasks((prev) => prev.filter((t) => t.id !== id));
    try {
      await tasksApi.permanentDelete(id);
      toast.success("Görev kalıcı olarak silindi");
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Kalıcı silme yetkiniz yok" : "Silme başarısız");
      load();
    }
  };

  // İptal grubu — seçerek / toplu kalıcı silme (tek istek, sunucu tarafında).
  const [cancelSel, setCancelSel] = useState(() => new Set());
  const cancelPickMode = showArchived && archiveGroup === "cancelled" && !!caps.perm_delete;
  const toggleCancelSel = (id) =>
    setCancelSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const runCancelledBulkDelete = async ({ all }) => {
    const ids = all ? null : Array.from(cancelSel);
    const count = all ? (archiveCounts.cancelled || tasks.length) : ids.length;
    if (!all && !count) return;
    if (all) {
      const typed = await promptDialog({
        title: "Tümünü Kalıcı Sil",
        message: `${count} iptal edilmiş görev kalıcı olarak silinecek. Bu işlem geri alınamaz. Onaylamak için SİL yazın.`,
        confirmText: "Kalıcı Sil",
        cancelText: "Vazgeç",
        danger: true,
      });
      if ((typed || "").trim().toLocaleUpperCase("tr") !== "SİL") {
        if (typed !== null) toast.error("Onay metni 'SİL' değil — hiçbir şey silinmedi");
        return;
      }
    } else {
      const ok = await confirmDialog({
        title: "Seçilenleri Kalıcı Sil",
        message: `${count} iptal edilmiş görev kalıcı olarak silinecek. Bu işlem geri alınamaz. Emin misiniz?`,
        confirmText: "Kalıcı Sil",
        cancelText: "Vazgeç",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      const res = await tasksApi.permanentDeleteCancelled({ ids, all });
      setCancelSel(new Set());
      toast.success(
        res.skipped
          ? `${res.deleted} görev kalıcı silindi · ${res.skipped} görev atlandı`
          : `${res.deleted} görev kalıcı olarak silindi`,
      );
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Kalıcı silme yetkiniz yok" : "Silme başarısız");
      load();
    }
  };

  // Çöp Kutusunu Boşalt (yalnızca admin).
  const emptyTrash = async () => {
    const ok = await confirmDialog({
      title: "Çöp Kutusunu Boşalt",
      message: "Çöp kutusundaki TÜM görevler kalıcı olarak silinecek. Bu işlem geri alınamaz. Emin misiniz?",
      confirmText: "Kalıcı Sil",
      danger: true,
    });
    if (!ok) return;
    try {
      const r = await tasksApi.emptyTrash(taskScope);
      toast.success(`${r.deleted || 0} görev kalıcı olarak silindi`);
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Yetkiniz yok" : "Boşaltma başarısız");
    }
  };

  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(i);
  }, []);

  // Fire reminders
  const fireReminder = useCallback(
    async (task) => {
      showReminderToast(task);
      beep();
      const repeatLeft = task.reminder_repeat_left;
      const intervalMin = task.reminder_interval_min;
      const isRecurring = intervalMin && repeatLeft && repeatLeft > 1;
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          new Notification("SERTEX Hatırlatma", {
            body: task.title + (task.description ? "\n" + task.description : ""),
            icon: "/favicon.ico",
            tag: `sertex-${task.id}-${repeatLeft || 0}`,
          });
        } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
      }
      try {
        if (isRecurring) {
          // Tekrarlı — bir sonraki hatırlatmayı planla, kalan sayıyı azalt.
          const nextIso = new Date(Date.now() + intervalMin * 60 * 1000).toISOString();
          await tasksApi.rescheduleReminder(task.id, nextIso, repeatLeft - 1);
        } else {
          // Son tekrar (veya tekrarsız) — hatırlatmayı bitir.
          await tasksApi.markReminderFired(task.id);
        }
        load();
      } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
    },
    [load, beep]
  );

  // Fire subtask reminders (when its due_date arrives)
  const fireSubReminder = useCallback(
    async (task, sub) => {
      if (!sub) return;
      toast(
        <div>
          <div className="font-mono text-sertex-cyan neon-glow mb-1">🔔 ALT GÖREV HATIRLATMA</div>
          <div className="font-mono text-xs text-sertex-textMuted">{task.title}</div>
          <div className="font-mono text-sm">↳ {sub.text}</div>
        </div>,
        { duration: 15000 }
      );
      beep();
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          new Notification("SERTEX · Alt görev", {
            body: `${task.title}\n↳ ${sub.text}`,
            icon: "/favicon.ico",
            tag: `sertex-sub-${sub.id}`,
          });
        } catch (e) { console.warn("[TasksPanel.jsx] hata bastırıldı:", e); }
      }
      // Persist reminder_fired=true on this subtask (iç içe olsa da id ile bulunur)
      const nextSubs = updateSubById(task.subtasks || [], sub.id, { reminder_fired: true });
      // Optimistic update in state
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, subtasks: nextSubs } : t)));
      try {
        await tasksApi.setSubtasks(task.id, nextSubs);
      } catch (e) {
        // ignore, will retry on next interval
      }
    },
    [beep]
  );

  useReminderScheduler(tasks, fireReminder, fireSubReminder);

  // Global gözcü (ReminderWatcher) ile çift tetiklemeyi önlemek için: bu panel
  // mount'luyken sayaç > 0 olur ve gözcü PAS geçer (firing'i panel yapar).
  useEffect(() => {
    window.__sertexTaskPanels = (window.__sertexTaskPanels || 0) + 1;
    return () => {
      window.__sertexTaskPanels = Math.max(0, (window.__sertexTaskPanels || 1) - 1);
    };
  }, []);

  // FAZ F — çift gönderim kilidi (gömülü "Yeni Görev" formu).
  const ekleKilidi = useRef(false);
  const [ekleniyor, setEkleniyor] = useState(false);

  // FAZ D — taslak alt görev ağacını kaydet (tek PATCH: hiyerarşi + sıra).
  const saveSubtasks = useCallback(async (taskId) => {
    const derlenmis = subsRef.current?.derle?.();
    if (!derlenmis || !derlenmis.agac.length) {
      if (derlenmis?.atlanan) toast(`${derlenmis.atlanan} boş alt görev atlandı`);
      return true;
    }
    const { agac, atlanan, tasinan } = derlenmis;
    try {
      await tasksApi.setSubtasks(taskId, agac);
      setFailedSubtasks(null);
      if (atlanan > 0) {
        toast(
          tasinan > 0
            ? `${atlanan} boş alt görev atlandı, altındaki ${tasinan} öğe üst seviyeye taşındı`
            : `${atlanan} boş alt görev atlandı`,
        );
      }
      return true;
    } catch {
      // KISMİ BAŞARISIZLIK: görev oluştu, alt görevler gitmedi. Veri BELLEKTE
      // kalır; hangi satırların gitmediği listelenir + "TEKRAR DENE".
      const isimler = [];
      const gez = (l) => l.forEach((n) => { isimler.push(n.text); gez(n.children || []); });
      gez(agac);
      setFailedSubtasks(isimler);
      toast.error("Alt görevler kaydedilemedi — 'TEKRAR DENE' ile yeniden gönderin");
      return false;
    }
  }, []);

  const retrySubtasks = useCallback(async () => {
    if (!createdIdRef.current || ekleKilidi.current) return;
    ekleKilidi.current = true;
    setEkleniyor(true);
    const ok = await saveSubtasks(createdIdRef.current);
    ekleKilidi.current = false;
    setEkleniyor(false);
    if (ok) {
      toast.success("Alt görevler kaydedildi");
      setShowAddForm(false);
      load();
      notifyTasksChanged();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveSubtasks]);

  // FAZ F — form içeriği kirli mi? Başlangıç anlık görüntüsü form AÇILIRKEN alınır.
  const anlikForm = useCallback(() => ({
    title, description, startDate, dueDate, assigneeName, companyName,
    assigneeUserIds, newCategoryId, newReminderDays, newReminderDisabled,
    newReminder, newAssignSerial, newShowDate,
    pendingFiles: pendingFiles.map((f) => f.name),
  }), [title, description, startDate, dueDate, assigneeName, companyName,
    assigneeUserIds, newCategoryId, newReminderDays, newReminderDisabled,
    newReminder, newAssignSerial, newShowDate, pendingFiles]);
  const addBaslangicRef = useRef(null);
  useEffect(() => {
    if (showAddForm) {
      addBaslangicRef.current = anlikForm();
      // Panel unmount olmadığı için teklif form AÇILIRKEN tazelenir.
      setAddTeklif(taslakOku(user?.id, "detayli-add-task"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddForm]);

  // FAZ G — taslak kurtarma (Detaylı gömülü form).
  const kirliAddForm = useCallback(
    () => kirliMi(anlikForm(), addBaslangicRef.current) || !!subsRef.current?.isimliVarMi?.(),
    [anlikForm],
  );
  const addTaslakTopla = useCallback(() => ({
    alanlar: anlikForm(),
    dosyaAdlari: pendingFiles.map((f) => f.name),
    altGorevler: subsRef.current?.taslakAl?.() || [],
  }), [anlikForm, pendingFiles]);
  const { teklif: addTeklif, setTeklif: setAddTeklif, yaz: addTaslakYaz, sil: addTaslakSil } = useFormDraft({
    userId: user?.id, formKey: "detayli-add-task", topla: addTaslakTopla, kirli: kirliAddForm,
  });

  const addTaslakGeriYukle = useCallback((veri) => {
    const a = veri?.alanlar || {};
    setTitle(a.title || "");
    setDescription(a.description || "");
    setStartDate(a.startDate || "");
    setDueDate(a.dueDate || "");
    setAssigneeName(a.assigneeName || "");
    setCompanyName(a.companyName || "");
    setAssigneeUserIds(a.assigneeUserIds || []);
    setNewCategoryId(a.newCategoryId || "");
    setNewReminderDays(a.newReminderDays ?? null);
    setNewReminderDisabled(!!a.newReminderDisabled);
    if (a.newReminder) setNewReminder(a.newReminder);
    setNewAssignSerial(!!a.newAssignSerial);
    setNewShowDate(!!a.newShowDate);
    subsRef.current?.taslakYukle?.(veri?.altGorevler || []);
    setAddTeklif(null);
    const dosyalar = veri?.dosyaAdlari || [];
    if (dosyalar.length) {
      toast(`${dosyalar.length} dosya geri yüklenemedi, tekrar seçmeniz gerekiyor`, {
        description: dosyalar.join(", "), duration: 8000,
      });
    }
    toast.success("Taslak geri yüklendi");
  }, [setAddTeklif]);

  // Kapatma uyarısı — girilen her bilgi (başlık, tarih, dosya, alt görev…) için.
  const closeAddForm = useCallback(async () => {
    if (kirliAddForm()) {
      const ok = await confirmDialog(KIRLI_UYARI);
      if (!ok) return;
      if (addTaslakYaz()) {
        toast("Taslak kaydedildi", {
          action: {
            label: "GERİ YÜKLE",
            onClick: () => { setShowAddForm(true); const t = taslakOku(user?.id, "detayli-add-task"); if (t?.veri) addTaslakGeriYukle(t.veri); },
          },
          duration: 8000,
        });
      }
    }
    setShowAddForm(false);
  }, [kirliAddForm, addTaslakYaz, addTaslakGeriYukle, user]);

  useEffect(() => {
    if (!showAddForm) return;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); closeAddForm(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAddForm, closeAddForm]);

  const addTask = async () => {
    if (ekleKilidi.current) return;
    if (!title.trim()) {
      toast.error("Başlık gerekli");
      return;
    }
    // Yumuşak doğrulama — başlangıç, bitişten sonra olamaz (ikisi de opsiyonel).
    if (startDate && dueDate && new Date(startDate) > new Date(dueDate)) {
      toast.error("Başlangıç tarihi bitiş tarihinden sonra olamaz");
      return;
    }
    ekleKilidi.current = true;
    setEkleniyor(true);
    try {
      // Görev Paylaşımı — assignment routing (self may be included):
      //   0 seçili veya sadece "kendim" → kişisel görev (extras yok).
      //   yalnız 1 BAŞKA kişi → tekil devir (assignee_user_id).
      //   2+ (kendim dahil olabilir) → çok kişili görev (assignee_user_ids).
      const ids = assigneeUserIds;
      const others = ids.filter((x) => x !== user?.id);
      const includesSelf = user?.id ? ids.includes(user.id) : false;
      const extras = {
        assignee_name: assigneeName.trim() || null,
        company_name: companyName.trim() || null,
      };
      let mode = "self";
      if (ids.length === 0 || (ids.length === 1 && includesSelf)) {
        mode = "self";
      } else if (others.length === 1 && !includesSelf) {
        mode = "single";
        extras.assignee_user_id = others[0];
      } else {
        mode = "multi";
        extras.assignee_user_ids = ids;
      }
      if (newCategoryId) {
        extras.category_id = newCategoryId;
      }
      if (newAssignSerial) extras.assign_serial = true;
      if (newShowDate) extras.show_created_date = true;
      if (startDate) {
        extras.start_date = new Date(startDate).toISOString();
      }
      // Faz 8 CP5 — apply due-soon overrides from the form.
      if (newReminderDisabled) {
        extras.reminder_disabled = true;
      } else if (newReminderDays != null) {
        extras.reminder_days = newReminderDays;
      }
      // Tekrarlı hatırlatıcı (ilk zaman + kaç defa + aralık).
      const rr = resolveRecurringReminder(newReminder);
      if (rr.error) {
        toast.error(rr.error);
        return;
      }
      if (rr.reminder_at) {
        extras.reminder_interval_min = rr.reminder_interval_min;
        extras.reminder_repeat_total = rr.reminder_repeat_total;
        extras.reminder_repeat_left = rr.reminder_repeat_left;
      }
      const created = await tasksApi.create(
        title.trim(),
        description.trim(),
        dueDate ? new Date(dueDate).toISOString() : null,
        rr.reminder_at || null,
        extras,
      );
      // Bekleyen dosyaları yeni göreve yükle (parçalı). Görev zaten oluştu;
      // dosya hatası olsa bile görev kaybolmaz — sadece uyarı gösterilir.
      if (created?.id && pendingFiles.length) {
        let okCount = 0;
        for (const f of pendingFiles) {
          try {
            await taskAttachmentsApi.upload(created.id, f);
            okCount += 1;
          } catch (err) {
            toast.error(`Dosya yüklenemedi: ${f.name}`);
          }
        }
        if (okCount) toast.success(`${okCount} dosya göreve eklendi`);
      }
      const firstOther = teamMembers.find((m) => m.id === others[0]);
      // FAZ D — alt görevler: görev ve dosyalardan SONRA, aynı görev id'siyle.
      if (created?.id) {
        createdIdRef.current = created.id;
        const subsOk = await saveSubtasks(created.id);
        if (!subsOk) { load(); notifyTasksChanged(); return; }
      }
      setTitle("");
      setDescription("");
      setDueDate("");
      setStartDate("");
      setAssigneeName("");
      setCompanyName("");
      setCompanyAutoFilled(false);
      setAssigneeUserIds([]);
      setPendingFiles([]);
      setNewCategoryId("");
      setNewAssignSerial(false);
      setNewShowDate(false);
      setNewReminderDays(null);
      setNewReminderDisabled(false);
      setNewReminder(defaultRecurringValue());
      setShowAddForm(false);
      addTaslakSil();
      load();
      notifyTasksChanged();
      toast.success(
        mode === "multi"
          ? `Görev ${ids.length} kişiye atandı${includesSelf ? " (siz dahil)" : ""}`
          : mode === "single" && firstOther
          ? `Görev ${firstOther.username} kullanıcısına atandı`
          : "Görev eklendi"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    } finally {
      ekleKilidi.current = false;
      setEkleniyor(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await tasksApi.setStatus(id, status);
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error("Güncellenemedi");
    }
  };

  const removeTask = async (id) => {
    smartDeleteIdRef.current = null;
    const t = tasks.find((x) => x.id === id);
    if (smartDelete.requestDelete(t)) { smartDeleteIdRef.current = id; return; } // alt görevi var → akıllı silme diyaloğu
    const res = await askReason("delete");
    if (!res.ok) return;
    closeWindowsForTask(id);
    try {
      await tasksApi.delete(id, res.reason);
      load();
      notifyTasksChanged();
      toast.success("Çöp kutusuna taşındı");
    } catch (e) {
      // Faz 9 CP4.27 — 423 = task locked. Show the actionable detail from the
      // backend instead of a generic "Silinemedi" so the user knows to ask
      // their manager for an OTP.
      if (e?.response?.status === 423) {
        toast.error(e.response.data?.detail || "Görev kilitli — müdürünüzden şifre isteyin");
      } else {
        toast.error("Silinemedi");
      }
    }
  };

  // Faz 8 CP3 — transfer ownership. Called by TaskCard → ReassignModal.
  const reassignTask = async (id, newOwnerId) => {
    await tasksApi.reassign(id, newOwnerId);
    load();
    notifyTasksChanged();
  };

  // Şirkete Devret — görevi bir şirkete aktar (sahipsiz + kolsuz orphan).
  const transferTaskToCompany = async (id, companyId) => {
    await tasksApi.transferToCompany(id, companyId);
    load();
    notifyTasksChanged();
  };

  // Alt görevi tam bir göreve dönüştür (promote) → sunucuda oluştur + listeyi
  // yenile. Ana görevden alt görev çıkar; yeni görev "alt unsuru" rozetli gelir.
  const promoteSubtask = async (taskId, subId, moveChildIds = null) => {
    try {
      const created = await tasksApi.promoteSubtask(taskId, subId, moveChildIds);
      toast.success(`Alt görev göreve dönüştürüldü: ${created?.title || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Göreve dönüştürülemedi");
    }
    load();
    notifyTasksChanged();
  };

  // Promote'u geri al → görevi ana görevin alt görevine geri çevir.
  const demoteToSubtask = async (taskId) => {
    try {
      await tasksApi.demoteToSubtask(taskId);
      toast.success("Görev tekrar alt göreve dönüştürüldü");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alt göreve dönüştürülemedi");
    }
    load();
    notifyTasksChanged();
  };

  // Sıra numarası sabitle/kaldır. pinned=true iken number verilir (otomatik veya
  // elle). Elle aynı numara başka bir sabit göreve verilmişse engellenir.
  const setTaskPin = async (taskId, pinned, number) => {
    if (pinned && number != null) {
      const dup = tasks.find(
        (t) => t.id !== taskId && t.status !== "done" && t.number_pinned && t.pinned_number === number,
      );
      if (dup) {
        toast.error(`${number} numarası zaten "${dup.title}" görevine sabit`);
        return;
      }
    }
    try {
      await tasksApi.update(taskId, {
        number_pinned: pinned,
        pinned_number: pinned ? number : null,
      });
      toast.success(pinned ? `Sıra numarası sabitlendi: ${number}` : "Sıra numarası sabiti kaldırıldı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
    load();
    notifyTasksChanged();
  };

  // Dürt / Hatırlat — personele görev hatırlatması gönder (çan + push).
  const [nudgeCounts, setNudgeCounts] = useState({});
  const nudgeTask = async (id, task) => {
    try {
      const r = await tasksApi.nudge(id);
      const n = r?.count_today || 0;
      if (n > 0) setNudgeCounts((m) => ({ ...m, [id]: n }));
      toast.success(
        n > 1
          ? `Hatırlatma gönderildi · bugün ${n}. kez`
          : `Hatırlatma gönderildi: ${task?.assignee_name || "personel"}`
      );
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.warning(detail || "Çok sık — biraz sonra tekrar deneyin");
      else toast.error(detail || "Hatırlatma gönderilemedi");
    }
  };

  // Faz 8 CP4 — move a task into (or out of) an iş kolu.
  const setTaskCategory = async (id, categoryId) => {
    try {
      // Backend accepts empty string for "clear" via the special-cased branch.
      await tasksApi.update(id, { category_id: categoryId || "" });
      const name = categoryId
        ? (categories.find((c) => c.id === categoryId)?.name || "İş Kolu")
        : "Kolsuz";
      toast.success(`Görev → ${name}`);
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Değiştirilemedi");
    }
  };

  // Görev Kopyalama — panodaki görevi hedef iş koluna (categoryId; null =
  // KOLSUZ) çoğalt. Pano temizlenene kadar durur → çok kez yapıştırılabilir.
  // "Kopyala" — seçili görevi ANINDA panoya al (alt görevler + dosyalar dahil).
  // Eskiden bir onay penceresi açılıyordu; kullanıcı onaylamadan kapatınca eski
  // görev panoda kalıp yanlış yapıştırılıyordu. Artık tek tık = doğru kopya.
  const copyTaskToClipboard = (t) => {
    setTaskClipboard({
      sourceId: t.id,
      title: t.title,
      includeSubtasks: (t.subtasks || []).length > 0,
      includeAttachments: true,
    });
    toast.success(`Panoya kopyalandı: ${t.title} — bir iş koluna sağ tıklayıp Yapıştır'ı seçin.`);
  };

  const handlePaste = async (categoryId, categoryName) => {
    if (!clipboard?.sourceId) return;
    try {
      await tasksApi.duplicate(clipboard.sourceId, {
        include_subtasks: !!clipboard.includeSubtasks,
        include_attachments: !!clipboard.includeAttachments,
        category_id: categoryId || null,
      });
      toast.success(`Yapıştırıldı → ${categoryName || "Kolsuz"}`);
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yapıştırılamadı");
    }
  };

  // Şablondan görev oluştur (instantiate) → oluşan görevi Düzenle'de aç.
  const handleUseTemplate = (task) => {
    load();
    notifyTasksChanged();
    if (task) setEditing(task);
  };

  // Faz 8 CP5 — task-level due-soon overrides via context menu.
  const setTaskReminderDays = async (id, days) => {
    try {
      // null → clear override (fall back to hierarchy). Whitelisted server-side.
      await tasksApi.update(id, {
        reminder_days: days == null ? 0 : days,
        reminder_disabled: false,
      });
      toast.success(
        days == null
          ? "Uyarı: varsayılan (hiyerarşi)"
          : `Uyarı: ${days} gün önce`,
      );
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error("Değiştirilemedi");
    }
  };
  const setTaskReminderDisabled = async (id, disabled) => {
    try {
      await tasksApi.update(id, { reminder_disabled: !!disabled });
      toast.success(disabled ? "Bu görev için hatırlatıcı kapatıldı" : "Hatırlatıcı yeniden aktif");
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error("Değiştirilemedi");
    }
  };
  const setTaskDigestMuted = async (id, muted) => {
    try {
      await tasksApi.update(id, { digest_muted: !!muted });
      toast.success(muted ? "Bu görev sabah özetinden çıkarıldı" : "Bu görev sabah özetine eklendi");
      load();
      notifyTasksChanged();
    } catch (e) {
      toast.error("Değiştirilemedi");
    }
  };

  const saveEditFor = async (taskId, patch) => {
    try {
      await tasksApi.update(taskId, patch);
      load();
      notifyTasksChanged();
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
      throw e;
    }
  };
  const saveEdit = async (patch) => saveEditFor(editing.id, patch);

  const setReminder = async (id, iso, opts = {}) => {
    try {
      await tasksApi.setReminder(id, iso, opts);
      load();
      const d = new Date(iso);
      const base = `Hatırlatma kuruldu: ${d.toLocaleString("tr-TR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })}`;
      toast.success(opts.repeatTotal > 1 ? `${base} · ${opts.repeatTotal} defa tekrar` : base);
    } catch (e) {
      toast.error("Hatırlatma kurulamadı");
    }
  };

  const clearReminder = async (id) => {
    try {
      await tasksApi.setReminder(id, null);
      load();
      toast.success("Hatırlatma iptal edildi");
    } catch (e) {
      toast.error("İptal edilemedi");
    }
  };

  const setSubtasks = async (id, subtasks, opts = {}) => {
    // Faz 9 CP4.34 — task-scoped rollback. Previously a failed setSubtasks
    // called `load()` which reset the ENTIRE task list, wiping any other
    // concurrent optimistic updates (drag order, status flips, etc.). Now
    // we snapshot only the affected task's previous subtasks and revert
    // just that field on failure.
    let prevSubtasks;
    setTasks((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      if (prevSubtasks === undefined) prevSubtasks = t.subtasks;
      return { ...t, subtasks };
    }));
    // `localOnly`: alt görev TARİH/TAMAMLAMA akışları kendi HEDEFLİ ucunu
    // kullanır (tek düğüm yazar). Burada tüm ağacı tekrar PATCH etmek o
    // korumayı boşa çıkarır, bu yüzden yalnızca ekran durumu güncellenir.
    if (opts.localOnly) return;
    try {
      await tasksApi.setSubtasks(id, subtasks);
    } catch (e) {
      toast.error("Alt görev kaydedilemedi");
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, subtasks: prevSubtasks } : t)));
    }
  };

  const handleReorderTasks = async (nextTasks) => {
    // Faz 9 CP8.3 — CRITICAL FIX: previously `setTasks(nextTasks)` alone
    // was insufficient because the derived `sorted` memo re-sorts by each
    // task's `sort_order` (desc). Since the dragged tasks still carried
    // their OLD sort_order values, the sort silently reverted the user's
    // drag order on the very next render — making reorder appear broken
    // ("doesn't stick"). We now stamp each row with the NEW sort_order
    // locally (matching the backend formula `n - idx`) so the derived
    // `sorted` view stays in sync until the backend confirms.
    const n = nextTasks.length;
    const now = new Date().toISOString();
    const stamped = nextTasks.map((t, idx) => ({
      ...t,
      sort_order: n - idx,
      updated_at: now,
    }));
    setTasks(stamped);
    try {
      await tasksApi.reorder(nextTasks.map((t) => t.id));
    } catch (e) {
      toast.error("Sıra kaydedilemedi");
      load();
    }
  };

  // Faz 9 CP4.31 — When a category filter is active we can still drag-reorder
  // within the filtered subset. This helper preserves the positions of
  // non-visible tasks — only the filtered rows shuffle among their original
  // slots in the master list. Backend then persists the full new order.
  //
  // Faz 9 CP8.2 — CRITICAL FIX: we index against `sorted` (the frontend's
  // canonical UI order), NOT `tasks` (raw API response). Reorder.Group is
  // fed with `filtered = sorted.filter(...)`, so nextFiltered maps back to
  // positions in `sorted`. Using `tasks` here made the mapping drift when
  // the backend's created_at fallback disagreed with the frontend's
  // status/due-date score — reorder appeared to "not stick" for users
  // with a mix of ordered + unordered tasks under AKTİF/GEÇTİ filters.
  const handleReorderFiltered = async (nextFiltered) => {
    const visibleIds = new Set(nextFiltered.map((t) => t.id));
    const visiblePositions = [];
    sorted.forEach((t, idx) => { if (visibleIds.has(t.id)) visiblePositions.push(idx); });
    if (visiblePositions.length !== nextFiltered.length) {
      // Safety net — fallback to a full reload if something desyncs.
      load();
      return;
    }
    const next = [...sorted];
    visiblePositions.forEach((pos, i) => { next[pos] = nextFiltered[i]; });
    await handleReorderTasks(next);
  };

  const sorted = showArchived
    ? [...tasks].sort((a, b) => {
        // Arşiv sıralaması: tarih (silme/iptal/arşiv zamanı) veya A-Z.
        if (archiveSort === "az") return (a.title || "").localeCompare(b.title || "", "tr");
        const dt = (t) => t.deleted_at || t.cancelled_at || t.archived_at || t.updated_at || "";
        const da = dt(a), db2 = dt(b);
        if (da === db2) return 0;
        return archiveSort === "old" ? (da < db2 ? -1 : 1) : (da > db2 ? -1 : 1);
      })
    : [...tasks].sort((a, b) => {
    // Faz 9 CP4.32 — user drag order wins. Backend's /tasks/reorder assigns
    // `sort_order = n - idx` (desc = first). Any task with a persisted
    // sort_order sorts among itself by that value; the remainder falls back
    // to the legacy status/due-date scoring so brand-new tasks still land in
    // a sensible spot.
    const ao = a.sort_order;
    const bo = b.sort_order;
    if (ao != null && bo != null) {
      if (ao !== bo) return bo - ao; // desc
    } else if (ao != null) return -1;
    else if (bo != null) return 1;
    const scoreOf = (t) => {
      if (isOverdue(t)) return 0;
      if (t.status === "pending") return 1;
      if (t.status === "paused") return 2;
      return 3;
    };
    const sa = scoreOf(a);
    const sb = scoreOf(b);
    if (sa !== sb) return sa - sb;
    if (a.due_date && b.due_date) return new Date(a.due_date) - new Date(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return 0;
  });

  // "Bu görevden çıkanlar" — promote ile bu görevden türeyen görevleri
  // yüklü listeden (ekstra istek yok) ana görev id'sine göre grupla.
  // Her çocuğa gerçek durum kovası (__bucket) eklenir: gecti(kırmızı)/
  // bitti(yeşil)/bekliyor(sarı)/aktif(cyan) — overdue hesaplanır.
  const promotedChildrenMap = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      const pid = t.promoted_from_task_id;
      if (!pid) continue;
      const bucket = isOverdue(t)
        ? "gecti"
        : t.status === "done"
        ? "bitti"
        : t.status === "paused"
        ? "bekliyor"
        : "aktif";
      (map[pid] = map[pid] || []).push({ ...t, __bucket: bucket });
    }
    return map;
  }, [tasks]);

  const bucketOf = (t) => {
    if (isOverdue(t)) return "gecti";
    if (t.status === "done") return "bitti";
    if (t.status === "paused") return "bekliyor";
    return "aktif";
  };

  // ================= GÖREV BAĞLAMA (Task Groups) =================
  const groupById = useMemo(
    () => Object.fromEntries((groups || []).map((g) => [g.id, g])),
    [groups]
  );
  // Numaralandırma (done olmayanlar sıralı) — sabitlenmiş numaralar atlanır.
  // Sabit görev kendi pinned_number'ını gösterir; dinamik görevler bu
  // numaraları rezerve kabul edip atlar (çakışma olmaz).
  const numById = useMemo(() => {
    const m = {};
    const reserved = new Set();
    for (const t of sorted) {
      if (t.status !== "done" && t.number_pinned && t.pinned_number != null) {
        reserved.add(t.pinned_number);
      }
    }
    let c = 0;
    for (const t of sorted) {
      if (t.status === "done") { m[t.id] = null; continue; }
      if (t.number_pinned && t.pinned_number != null) { m[t.id] = t.pinned_number; continue; }
      c += 1;
      while (reserved.has(c)) c += 1;
      m[t.id] = c;
    }
    return m;
  }, [sorted]);

  // Ana görev ÇOKLU SEÇİM + toplu işlem (Sabitle mevcut sıra numarasına).
  const taskBulk = useTaskBulk({
    numberFor: (id) => numById[id],
    titleFor: (id) => tasks.find((t) => t.id === id)?.title,
    refresh: () => { load(); notifyTasksChanged(); },
    onEditQueue: (ids) => editQueue.basla(ids, visibleTaskListRef.current),
  });
  // AŞAMA 2 — sıralı düzenleme kuyruğu (bitince seçim modu temizlenir).
  const editQueue = useEditQueue({ tasks, onFinish: () => taskBulk.exit(), onReload: () => load() });
  // AŞAMA 1 — TAŞI (menü yolu): ana görev + alt görev toplu taşıma.
  const taskMove = useTaskMove({ tasks, refresh: () => { load(); notifyTasksChanged(); } });

  // FAZ 2B — SÜRÜKLE-BIRAK ile "içine taşı".
  // Geçersiz hedefler: sürüklenen kalemin kendisi, (alt görev sürükleniyorsa)
  // kendi alt ağacındaki her kalem ve sürüklenen ana görevin TÜM alt görevleri
  // (sınırsız derinlik) — böylece görev kendi alt görevinin içine bırakılamaz.
  // Dış listede sürükleme kimliği satır anahtarıdır ("t:<id>"); görev kimliğine indir.
  const gorevIdOf = (srcId) => (typeof srcId === "string" && srcId.startsWith("t:") ? srcId.slice(2) : srcId);

  const gecersizBirakmaIdleri = useCallback((rawId, srcData) => {
    const srcId = gorevIdOf(rawId);
    const set = new Set();
    if (!srcId) return set;
    if (srcData?.kind === "subtask") {
      const t = tasks.find((x) => x.id === srcData.taskId);
      const node = flattenSubs(t?.subtasks || []).find((s) => s.id === srcId);
      set.add(srcId);
      for (const s of flattenSubs(node?.children || [])) set.add(s.id);
      return set;
    }
    const idler = taskBulk.ids?.includes(srcId) ? [...taskBulk.ids] : [srcId];
    for (const id of idler) {
      set.add(id);
      const t = tasks.find((x) => x.id === id);
      for (const s of flattenSubs(t?.subtasks || [])) set.add(s.id);
    }
    return set;
  }, [tasks, taskBulk.ids]);

  const surukleyerekTasi = useCallback((hedef, src) => {
    const hedefArgs = { targetId: hedef.taskId, targetSubId: hedef.subId || null };
    if (src?.data?.kind === "subtask") {
      // DOĞRUDAN EBEVEYNE bırakma: nokta görünür ama işlem YAPILMAZ (veri
      // bozulmasın); kullanıcıya "zaten burada" bilgisi verilir.
      const ayniKart = hedef.taskId === src.data.taskId;
      const mevcutEbeveyn = src.data.parentId || null;
      if (ayniKart && (hedef.subId || null) === mevcutEbeveyn) {
        toast.info("Görev zaten burada — taşıma yapılmadı");
        return;
      }
      // ÇOKLU SEÇİM: sürüklenen alt görev seçili kümedeyse tümü taşınır.
      const secili = src.data.selectedIds;
      const subIds = Array.isArray(secili) && secili.includes(src.id) ? [...secili] : [src.id];
      taskMove.suruklemeIleTasi({
        subMoves: [{ task_id: src.data.taskId, sub_ids: subIds }], ...hedefArgs,
      });
      return;
    }
    const sid = gorevIdOf(src.id);
    const idler = taskBulk.ids?.includes(sid) ? [...taskBulk.ids] : [sid];
    taskMove.suruklemeIleTasi({ taskIds: idler, ...hedefArgs });
  }, [taskMove, taskBulk.ids]);

  const surukleSayisi = useCallback((rawId, srcData) => {
    if (srcData?.kind === "subtask") {
      const s = srcData.selectedIds;
      return Array.isArray(s) && s.includes(rawId) ? s.length : 1;
    }
    const srcId = gorevIdOf(rawId);
    return taskBulk.ids?.includes(srcId) ? taskBulk.ids.length : 1;
  }, [taskBulk.ids]);

  const visibleTaskListRef = useRef([]);
  // Bağlı görevleri tek blok haline getir; sıra global sort_order'dan gelir.
  const groupRows = useMemo(() => {
    const rows = [];
    const byGid = {};
    for (const t of sorted) {
      const gid = t.group_id && groupById[t.group_id] ? t.group_id : null;
      if (gid) {
        let row = byGid[gid];
        if (!row) {
          row = { key: `g:${gid}`, type: "group", group: groupById[gid], tasks: [] };
          byGid[gid] = row;
          rows.push(row);
        }
        row.tasks.push(t);
      } else {
        rows.push({ key: `t:${t.id}`, type: "task", task: t });
      }
    }
    // Tek üyeli grup kalırsa (veri drift) blok gösterme, tekil satıra indir.
    return rows.map((r) =>
      r.type === "group" && r.tasks.length < 2 && r.tasks[0]
        ? { key: `t:${r.tasks[0].id}`, type: "task", task: r.tasks[0] }
        : r
    );
  }, [sorted, groupById]);
  const rowKeys = useMemo(() => groupRows.map((r) => r.key), [groupRows]);
  const rowByKey = useMemo(
    () => Object.fromEntries(groupRows.map((r) => [r.key, r])),
    [groupRows]
  );

  const cardPropsFor = (t) => ({
    task: t,
    displayNumber: numById[t.id],
    collapsed: collapsedIds.has(t.id),
    onToggleCollapse: () => toggleCollapse(t.id),
    onToggleDetach: () => toggleDetach(t.id),
    onStatusChange: (status) => setStatus(t.id, status),
    onDelete: () => removeTask(t.id),
    onEdit: () => setEditing(t),
    onCopy: () => copyTaskToClipboard(t),
    // Arşiv → İPTAL grubunda seçim, kalıcı silme seçimini (cancelSel) sürer.
    selectMode: cancelPickMode || taskBulk.selectMode,
    selected: cancelPickMode ? cancelSel.has(t.id) : taskBulk.has(t.id),
    onSelectToggle: () => (cancelPickMode ? toggleCancelSel(t.id) : taskBulk.toggle(t.id)),
    onSelect: () => taskBulk.start(t.id),
    onSetReminder: (iso, opts) => setReminder(t.id, iso, opts),
    onClearReminder: () => clearReminder(t.id),
    onSetSubtasks: (subs, opts) => setSubtasks(t.id, subs, opts),
    onSetArchived: (v) => setArchived(t.id, v),
    onReassign: (uid) => reassignTask(t.id, uid),
    onTransferCompany: (cid) => transferTaskToCompany(t.id, cid),
    onPromoteSubtask: (subId, moveChildIds) => promoteSubtask(t.id, subId, moveChildIds),
    onDemoteToSubtask: () => demoteToSubtask(t.id),
    onDemoteChild: (childId) => demoteToSubtask(childId),
    onPinNumber: (number) => setTaskPin(t.id, true, number),
    onUnpinNumber: () => setTaskPin(t.id, false, null),
    promotedChildren: promotedChildrenMap[t.id] || [],
    highlight: searchTerm,
    isTeamView,
    isHighlighted: highlightedTaskId === t.id,
    categoryName: categories.find((c) => c.id === t.category_id)?.name || null,
    categories,
    onSetCategory: (cid) => setTaskCategory(t.id, cid),
    reminderConfig,
    onSetReminderDays: (d) => setTaskReminderDays(t.id, d),
    onSetReminderDisabled: (v) => setTaskReminderDisabled(t.id, v),
    onToggleDigestMute: (v) => setTaskDigestMuted(t.id, v),
    currentUser: user,
    onLockChanged: () => { load(); notifyTasksChanged(); },
    onLinkTasks: () => setLinkModal({ mode: "create", taskId: t.id }),
    onEditGroup: () => t.group_id && setLinkModal({ mode: "edit", groupId: t.group_id }),
    onRemoveFromGroup: () => t.group_id && removeFromGroup(t.group_id, t.id),
    // Görev → İş Kolu sürükle-bırak (Reorder.Item wrapper tarafından kullanılır).
    onDragToCategory: handleTaskDragToCategory,
    onDropToCategory: (e, info) => handleTaskDropCategory(t, e, info),
    // Arşiv grupları — İptal Et / Geri Yükle / Kalıcı Sil.
    archiveGroup: showArchived ? archiveGroup : null,
    canPermanentDelete: !!caps.perm_delete,
    archiveSettings: { trash_autoclean_enabled: taskSettings.trash_autoclean_enabled, trash_autoclean_days: taskSettings.trash_autoclean_days },
    onCancel: () => cancelTask(t.id),
    onUncancel: () => uncancelTask(t.id),
    onRestore: () => restoreTask(t.id),
    onPermanentDelete: () => permanentDeleteTask(t.id),
    // AŞAMA 9 (Adım 2) — alt görev yüzen pencere durumu.
    detachedSubIds,
    onDockSubtask: (subId) => toggleSubDetach(subId),
    onDetachSubtask: (subId) => detachSubByDrag(subId, null),
    // TAŞI — Detaylı arayüzde ana liste kartları da taşıma menülerini görür.
    onMoveTask: (id) => taskMove.anaGorevleriTasi(
      taskBulk.ids?.includes(id) ? [...taskBulk.ids] : [id],
    ),
    onMoveSubtasks: (subIds) => taskMove.altGorevleriTasi(t.id, subIds),
    onPromoteSubtasks: (subIds) => taskMove.anaGoreveCikar(t.id, subIds),
  });

  const renderMember = (t, memberIndex = 0) => {
    if (detachedIds.has(t.id)) {
      return (
        <ReorderablePlaceholder
          key={t.id}
          task={t}
          index={memberIndex}
          displayNumber={numById[t.id]}
          onDock={() => toggleDetach(t.id)}
        />
      );
    }
    return <ReorderableTaskCard key={t.id} index={memberIndex} {...cardPropsFor(t)} />;
  };

  const handleReorderRows = async (nextKeys) => {
    const ids = [];
    for (const k of nextKeys) {
      const r = rowByKey[k];
      if (!r) continue;
      if (r.type === "group") ids.push(...r.tasks.map((t) => t.id));
      else ids.push(r.task.id);
    }
    const byId = Object.fromEntries(sorted.map((t) => [t.id, t]));
    const next = ids.map((id) => byId[id]).filter(Boolean);
    if (next.length === sorted.length) await handleReorderTasks(next);
  };

  const handleReorderGroup = async (gid, nextGroupTasks) => {
    const ids = [];
    for (const r of groupRows) {
      if (r.type === "group" && r.group.id === gid) ids.push(...nextGroupTasks.map((t) => t.id));
      else if (r.type === "group") ids.push(...r.tasks.map((t) => t.id));
      else ids.push(r.task.id);
    }
    const byId = Object.fromEntries(sorted.map((t) => [t.id, t]));
    const next = ids.map((id) => byId[id]).filter(Boolean);
    if (next.length === sorted.length) await handleReorderTasks(next);
  };

  const removeFromGroup = async (gid, tid) => {
    try {
      await tasksApi.removeGroupMember(gid, tid);
      toast.success("Görev gruptan çıkarıldı");
      load();
      notifyTasksChanged();
    } catch (e) {
      console.error("[TasksPanel] gruptan çıkarma hatası:", e);
      toast.error("Çıkarılamadı");
    }
  };

  const dissolveGroup = async (group) => {
    const gid = group?.id || group;
    if (!gid) return;
    const memberIds = sorted.filter((t) => t.group_id === gid).map((t) => t.id);
    const mode = await choiceDialog({
      title: "GRUBU ÇÖZ",
      message: `"${group.name || "Bağlı Görevler"}" grubu nasıl çözülsün?\nGörevler silinmez; yalnızca bağlantıları kaldırılır.`,
      choices: [
        { value: "temporary", label: "ANLIK ÇÖZ (GERİ ALINABİLİR)", testid: "group-dissolve-temporary" },
        { value: "permanent", label: "KALICI ÇÖZ", danger: true, testid: "group-dissolve-permanent" },
      ],
      cancelText: "VAZGEÇ",
    });
    if (!mode) return;
    const done = () => { load(); notifyTasksChanged(); };
    if (mode === "permanent") {
      try { await tasksApi.deleteGroup(gid); toast.success("Grup kalıcı çözüldü"); done(); }
      catch (e) { console.error("[TasksPanel] grup çözme hatası:", e); toast.error("İşlem başarısız"); }
      return;
    }
    const snapshot = { name: group.name || "", color: group.color || null, show_progress: group.show_progress !== false, task_ids: memberIds };
    try {
      await tasksApi.deleteGroup(gid);
      done();
      toast.success("Grup çözüldü", {
        description: "Yanlışlıkla mı oldu? Geri alabilirsin.",
        duration: 12000,
        action: {
          label: "Geri Al",
          onClick: async () => {
            try { await tasksApi.createGroup(snapshot); toast.success("Grup geri yüklendi"); done(); }
            catch { toast.error("Geri alınamadı"); }
          },
        },
      });
    } catch (e) { console.error("[TasksPanel] grup çözme hatası:", e); toast.error("İşlem başarısız"); }
  };

  // Modal aday görevleri + ön seçim.
  const linkCandidates = useMemo(() => {
    if (!linkModal) return { candidates: [], preselected: [], group: null };
    const ungrouped = sorted.filter((t) => !t.group_id && !t.archived);
    if (linkModal.mode === "edit" && linkModal.groupId) {
      const members = sorted.filter((t) => t.group_id === linkModal.groupId);
      return {
        candidates: [...members, ...ungrouped],
        preselected: members.map((t) => t.id),
        group: groupById[linkModal.groupId] || null,
      };
    }
    return {
      candidates: ungrouped,
      preselected: linkModal.taskId ? [linkModal.taskId] : [],
      group: null,
    };
  }, [linkModal, sorted, groupById]);
  // ================= /GÖREV BAĞLAMA =================

  const filtered = (() => {
    // Arşiv görünümünde sidebar'daki iş kolu (kategori) çipleri ve durum filtre
    // çipleri GİZLİDİR (bkz. !showArchived koşulları). Ancak categoryFilter /
    // filters state'i aktif görünümden kalabilir; bunlar arşive uygulanırsa
    // arşiv araması "seçili kola ait olmayan" görevleri bulamaz (bug). Arşivin
    // kendi grup çipleri + sıralama + araması olduğu için burada bu iki filtreyi
    // yok sayıyoruz — arama tüm arşiv grubunu kapsar.
    let list = (showArchived || filters.length === 0)
      ? sorted
      : sorted.filter((t) => filters.includes(bucketOf(t)));
    // Faz 8 CP4 · category filter chip. Hiyerarşi: bir ana iş kolu seçilince
    // o kolun + TÜM alt kollarının görevleri gösterilir. (Arşivde uygulanmaz.)
    if (!showArchived) {
      if (categoryFilter === "__none__") {
        list = list.filter((t) => !t.category_id);
      } else if (categoryFilter) {
        const ids = getDescendantIds(categoryFilter, categories);
        list = list.filter((t) => t.category_id && ids.has(t.category_id));
      }
    }
    // Personel Görevleri — filter to a single person, or show NOTHING when the
    // "Tümü" tick is cleared (personFilter === "__none__").
    if (taskScope === "team") {
      if (personFilter === "__none__") {
        list = [];
      } else if (personFilter) {
        list = list.filter((t) => t.user_id === personFilter);
      }
    }
    // Görev arama — başlık/açıklama/kişi/şirket/iş kolu adı/alt görev metni.
    const q = searchTerm.trim().toLocaleLowerCase("tr");
    if (q) {
      list = list.filter((t) => {
        const catName = categories.find((c) => c.id === t.category_id)?.name || "";
        const hay = [
          t.title,
          t.description,
          t.assignee_name,
          t.company_name,
          catName,
          t.serial != null ? String(t.serial) : null,
          ...(Array.isArray(t.subtasks) ? t.subtasks.map((s) => s?.text) : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("tr");
        return hay.includes(q);
      });
    }
    return list;
  })();

  // Personel Görevleri — distinct owners present in the loaded team tasks, so
  // we can render person filter chips with counts. Names resolved via
  // teamMembers (id → username), falling back to the task's assignee_name.
  const teamOwners = (() => {
    if (taskScope !== "team") return [];
    const nameById = {};
    for (const m of teamMembers) nameById[m.id] = m.username;
    const byId = {};
    for (const t of tasks) {
      const oid = t.user_id;
      if (!oid) continue;
      if (!byId[oid]) {
        byId[oid] = {
          id: oid,
          name: nameById[oid] || t.assignee_name || "Kullanıcı",
          count: 0,
          overdue: 0,
          active: 0,
          done: 0,
        };
      }
      const o = byId[oid];
      o.count++;
      if (t.status === "done") o.done++;
      else if (isOverdue(t)) o.overdue++;
      else if (t.status === "pending") o.active++;
    }
    // Sort: people with overdue tasks first (most behind on top), then by load.
    return Object.values(byId).sort(
      (a, b) => b.overdue - a.overdue || b.count - a.count
    );
  })();

  // Personel Görevleri — aggregate summary across all staff (header strip).
  const teamSummary = (() => {
    if (taskScope !== "team") return null;
    return teamOwners.reduce(
      (acc, o) => {
        acc.overdue += o.overdue;
        acc.active += o.active;
        acc.done += o.done;
        acc.people += 1;
        return acc;
      },
      { overdue: 0, active: 0, done: 0, people: 0 }
    );
  })();

  // Per-category progress. Hiyerarşi: bir kategorinin sayısı kendisi + tüm
  // alt kollarındaki görevleri kapsar (ana kol toplamı gösterir).
  // {catId: {total, done}} + __none__ + __all__.
  const categoryCounts = (() => {
    if (!categories.length) return null;
    const dt = {};
    const dd = {};
    for (const c of categories) { dt[c.id] = 0; dd[c.id] = 0; }
    let noneT = 0;
    let noneD = 0;
    let allD = 0;
    for (const t of sorted) {
      const isDone = t.status === "done";
      if (isDone) allD++;
      if (t.category_id && dt[t.category_id] !== undefined) {
        dt[t.category_id]++;
        if (isDone) dd[t.category_id]++;
      } else if (!t.category_id) {
        noneT++;
        if (isDone) noneD++;
      }
    }
    const m = {
      __none__: { total: noneT, done: noneD },
      __all__: { total: sorted.length, done: allD },
    };
    for (const c of categories) {
      let total = 0;
      let done = 0;
      for (const id of getDescendantIds(c.id, categories)) {
        total += dt[id] || 0;
        done += dd[id] || 0;
      }
      m[c.id] = { total, done };
    }
    return m;
  })();

  // Stat cards reflect the currently-scoped set (independent of the status
  // filter chips): in team scope they follow the selected person — or empty
  // when "Tümü" is unchecked (personFilter === "__none__").
  const statsBase = (() => {
    if (taskScope !== "team") return tasks;
    if (personFilter === "__none__") return [];
    if (personFilter) return tasks.filter((t) => t.user_id === personFilter);
    return tasks;
  })();

  const stats = {
    pending: statsBase.filter((t) => t.status === "pending" && !isOverdue(t)).length,
    done: statsBase.filter((t) => t.status === "done").length,
    paused: statsBase.filter((t) => t.status === "paused").length,
    overdue: statsBase.filter((t) => isOverdue(t)).length,
  };

  // Task 1 — "Tümünü Küçült / Büyüt" için o an görünen görev id'leri.
  const searchActive = !!searchTerm.trim();
  const visibleTaskList = taskScope === "team"
    ? filtered
    : ((showArchived || (filters.length === 0 && !categoryFilter)) && !searchActive ? sorted : filtered);
  const visibleTaskIds = visibleTaskList.map((t) => t.id);
  visibleTaskListRef.current = visibleTaskList;

  // Toplu "iş kolu ağaçlarını aç/kapat" — kartlara CustomEvent yayar; her kart
  // kendi durumunu ayarlar + KİŞİYE ÖZEL kalıcı olarak yazar (bozmadan).
  const [allTreesOpen, setAllTreesOpen] = useState(false);
  const hasHierCats = useMemo(() => categories.some((c) => c.parent_id), [categories]);
  // Görünen görevlerden HİYERARŞİK iş kolu olanların id'leri (ağaç açılabilir).
  const visibleHierTaskIds = useMemo(
    () => visibleTaskList
      .filter((t) => t.category_id && getCategoryPath(t.category_id, categories).length > 1)
      .map((t) => t.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleTaskList, categories]
  );
  // Buton etiketi/gerçek durum senkronu — kalıcı set'ten türetilir (reload
  // sonrası doğru etiket + tek tıkla doğru aksiyon).
  useEffect(() => {
    const sync = () => {
      const set = getCatTreeExpandedSet(user?.id);
      setAllTreesOpen(visibleHierTaskIds.length > 0 && visibleHierTaskIds.every((id) => set.has(id)));
    };
    sync();
    window.addEventListener("sertex:cattree-changed", sync);
    return () => window.removeEventListener("sertex:cattree-changed", sync);
  }, [visibleHierTaskIds, user?.id]);
  const toggleAllCatTrees = () => {
    // Gerçek durumu kalıcı set'ten oku; tersini uygula (reload sonrası no-op yok).
    const set = getCatTreeExpandedSet(user?.id);
    const allOpen = visibleHierTaskIds.length > 0 && visibleHierTaskIds.every((id) => set.has(id));
    const next = !allOpen;
    window.dispatchEvent(new CustomEvent("sertex:cattree-set-all", { detail: { expanded: next } }));
    setAllTreesOpen(next);
  };

  // ---- Sidebar İŞ KOLU FİLTRE AĞACI (tıklanabilir, açılır-kapanır liste) ----
  // Ağaç, orderedCategories sırasını korur (kardeş sürükle-sırala için).
  // Ata görünmüyorsa (my_tasks kapsamı) o düğüm kök gibi gösterilir (orphan).
  const catForest = useMemo(() => {
    const visibleIds = new Set(orderedCategories.map((c) => c.id));
    const childrenOf = (id) => orderedCategories.filter((c) => (c.parent_id || null) === id);
    const build = (node) => ({ ...node, children: childrenOf(node.id).map(build) });
    // AŞAMA 12 (Adım 2) — yüzen iş kolu penceresinde ağaç O KOLDAN köklenir:
    // kol + alt kolları (TAM AĞAÇ). Ana panelde davranış değişmez.
    if (detached && initialCategory) {
      const kok = orderedCategories.find((c) => c.id === initialCategory);
      return kok ? [build(kok)] : [];
    }
    return orderedCategories
      .filter((c) => !c.parent_id || !visibleIds.has(c.parent_id))
      .map(build);
  }, [orderedCategories, detached, initialCategory]);
  const [filterExpanded, setFilterExpanded] = useState(() => getCatFilterExpandedSet(user?.id));
  useEffect(() => {
    setFilterExpanded(getCatFilterExpandedSet(user?.id));
  }, [user?.id]);
  // AŞAMA 12 (Adım 2) — yüzen iş kolu penceresinde TAM AĞAÇ baştan AÇIK gelir
  // (kol + tüm alt kolları). Ana paneldeki kişisel aç/kapa tercihi bozulmaz.
  useEffect(() => {
    if (!detached || !initialCategory || !categories.length) return;
    setFilterExpanded((prev) => {
      const next = new Set(prev);
      for (const id of getDescendantIds(initialCategory, categories)) next.add(id);
      return next;
    });
  }, [detached, initialCategory, categories]);
  const toggleFilterNode = (id) => {
    const isOpen = filterExpanded.has(id);
    const next = new Set(filterExpanded);
    if (isOpen) next.delete(id);
    else next.add(id);
    setFilterExpanded(next);
    setCatFilterExpanded(user?.id, id, !isOpen);
  };
  // Filtre ağacında chevron gösteren (çocuğu olan, görünür) düğüm id'leri.
  const expandableFilterIds = useMemo(() => {
    const vis = new Set(orderedCategories.map((c) => c.id));
    const parents = new Set(
      orderedCategories.map((c) => c.parent_id).filter((pid) => pid && vis.has(pid))
    );
    return Array.from(parents);
  }, [orderedCategories]);
  const allFilterOpen =
    expandableFilterIds.length > 0 && expandableFilterIds.every((id) => filterExpanded.has(id));
  const toggleAllFilterNodes = () => {
    const next = allFilterOpen ? new Set() : new Set(expandableFilterIds);
    setFilterExpanded(next);
    saveCatFilterExpandedSet(user?.id, Array.from(next));
  };
  // AŞAMA 12 (Adım 2) — sağ tık menüsünü aç (pano boşken de açılır).
  const openCatMenu = (e, node) => {
    e.preventDefault();
    e.stopPropagation();
    setCatMenu({ x: e.clientX, y: e.clientY, id: node.id, name: node.name });
  };
  // AŞAMA 12 (Adım 3) — dnd-kit yolu KALICI (POC bayrağı kaldırıldı).
  // Reddedilen bırakmada (farklı seviye) dnd-kit'in geçici sıra dönüşümünü
  // temizlemek için sağlayıcıyı yeniden kur.
  const [catDndKey, setCatDndKey] = useState(0);
  // Ağaçta GÖRÜNEN (açık düğümlerin altındaki) kol id'leri — dnd-kit index'i
  // için düz liste. Boş iz (pencereye ayrılmış kol) sıralanamaz, listeye girmez.
  const visibleCatIds = useMemo(() => {
    const out = [];
    const walk = (nodes) => {
      for (const n of nodes) {
        out.push(n.id);
        if (n.children?.length && filterExpanded.has(n.id)) walk(n.children);
      }
    };
    walk(catForest);
    return out;
  }, [catForest, filterExpanded]);
  // dnd-kit bırakma sonucu → mevcut kural: SADECE aynı seviyedeki kardeşler.
  // `newIds` ağaçta GÖRÜNEN kolların yeni sırası; kardeş grubunun kendi içindeki
  // göreli sıra alınıp tam kategori sırasına uygulanır (ağaç yapısı korunur).
  const handleCatDndReorder = (newIds, sourceId) => {
    const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
    const sp = byId[sourceId]?.parent_id || null;
    const i = newIds.indexOf(sourceId);
    const komsular = [newIds[i - 1], newIds[i + 1]].filter(Boolean);
    if (!komsular.some((id) => (byId[id]?.parent_id || null) === sp)) {
      toast.info("Sadece aynı seviyedeki iş kolları arasında sıralama yapılabilir");
      setCatDndKey((k) => k + 1);
      return;
    }
    const kardesSira = newIds.filter((id) => (byId[id]?.parent_id || null) === sp);
    const kardesSet = new Set(kardesSira);
    const ids = orderedCategories.map((c) => c.id);
    const konumlar = ids.map((id, k) => (kardesSet.has(id) ? k : -1)).filter((k) => k >= 0);
    if (konumlar.length !== kardesSira.length) return;
    konumlar.forEach((pos, k) => { ids[pos] = kardesSira[k]; });
    persistCatOrder(ids);
  };
  // AŞAMA 12 (Adım 3) — kol tutamacından sürüklenip Neural Link panelinin
  // DIŞINA bırakıldı: kol, tam ağaç içerikli yüzen pencerede BIRAKILDIĞI
  // KOORDİNATTA açılır. Her seviyede (alt kollar dahil) geçerlidir.
  // Kategori başına tek pencere (dedupe) `openDetachedPanel` içinde.
  const handleCatDropOutside = (sourceId, pt) => {
    const node = categories.find((c) => c.id === sourceId);
    if (!node) return;
    // Pencereyi bir sonraki tick'te aç: dnd-kit sürükleme temizliğini
    // bitirmeden satırın yerine boş iz gelirse React removeChild ile çöküyor.
    setTimeout(() => {
      const panelId = openDetachedPanel({ category: sourceId, categoryName: node.name });
      if (pt?.x != null) placePanelAt(panelId, pt.x, pt.y);
      // Sıralama UYGULANMADI: dnd-kit'in geçici sıra dönüşümünü temizle,
      // yoksa ağaçta kol yanlış konumda görünüyor.
      setCatDndKey((k) => k + 1);
    }, 0);
  };
  const renderFilterNode = (node, depth) => {
    const hasKids = node.children && node.children.length > 0;
    const isOpen = filterExpanded.has(node.id);
    const p = categoryCounts && categoryCounts[node.id];
    const pct = p && p.total ? Math.round((p.done / p.total) * 100) : 0;
    const isActive = categoryFilter === node.id;
    // AŞAMA 12 (Adım 2/3) — kol pencereye ayrıldıysa listede BOŞ İZ kalır.
    // Sürüklenemez, ama "GERİ AL" düğmesi tıklanabilir. Yüzen pencerenin KENDİ
    // kök kolu boş iz olarak gösterilmez; dışarı alınmış ALT kollar gösterilir.
    const panelId = node.id === initialCategory ? null : detachedCatPanels.get(node.id);
    const placeholderInner = () => (
      <div
        style={{ marginLeft: depth * 14 }}
        onContextMenu={(e) => openCatMenu(e, node)}
        data-testid={`category-detached-placeholder-${node.name}`}
        className="flex items-center gap-2 rounded border border-dashed border-sertex-cyan/40 bg-sertex-cyan/5 px-2 py-1"
      >
        <Tag className="h-2.5 w-2.5 text-sertex-cyan shrink-0" />
        <span className="hud-text text-sertex-cyan/90 truncate flex-1 min-w-0">{node.name}</span>
        <span className="hud-text text-sertex-textMuted/70 text-[10px] hidden lg:inline">pencerede açık</span>
        <button
          onClick={() => closeDetachedPanel(panelId)}
          data-testid={`category-redock-${node.name}`}
          title="Pencereyi kapat, listeye geri al"
          className="shrink-0 px-1.5 py-0.5 rounded border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/15 hud-text flex items-center gap-1 transition-colors"
        >
          <Minimize2 className="h-3 w-3" /> GERİ AL
        </button>
      </div>
    );
    const rowInner = (handleRef) => (
      <div
        data-cat-drop={node.id}
        onContextMenu={(e) => openCatMenu(e, node)}
        data-testid={`category-node-${node.name}`}
        style={{ marginLeft: depth * 14 }}
        title="Tutamaçtan sürükleyerek aynı seviyede sırala · panel dışına bırakınca pencerede açılır · görevi buraya bırakınca bu iş koluna taşınır"
        className={`group flex items-center rounded border transition-colors ${
          taskDragOverCatId === node.id ? "ring-2 ring-sertex-cyan/80 bg-sertex-cyan/10" : ""
        } ${isActive ? "border-sertex-cyan bg-sertex-cyan/10" : "border-sertex-cyan/25"}`}
      >
        {hasKids ? (
          <button
            onClick={() => toggleFilterNode(node.id)}
            data-testid={`category-node-toggle-${node.name}`}
            title={isOpen ? "Alt kolları gizle" : "Alt kolları göster"}
            className="pl-1 py-1 text-sertex-textMuted hover:text-sertex-cyan shrink-0"
          >
            {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <span
          ref={handleRef}
          data-testid={`category-drag-${node.name}`}
          title="Sürükleyerek sırala · panel dışına bırakınca pencerede açılır"
          className="px-0.5 py-1 shrink-0 cursor-grab active:cursor-grabbing touch-none text-sertex-textMuted/70 hover:text-sertex-cyan"
        >
          <GripVertical className="h-2.5 w-2.5" />
        </span>
        <button
          onClick={() => setCategoryFilter(node.id)}
          data-testid={`category-chip-${node.name}`}
          className={`flex-1 min-w-0 pl-1 pr-2 py-1 hud-text flex items-center gap-1 text-left ${
            isActive ? "text-sertex-cyan" : "text-sertex-textMuted hover:text-sertex-cyan"
          }`}
        >
          <Tag className="h-2.5 w-2.5 shrink-0" />
          {node.color && <span className="h-2 w-2 rounded-full shrink-0" style={{ background: node.color }} />}
          <span className="truncate">{node.name}</span>
          {p && (
            <span
              className="inline-flex items-center gap-1 opacity-70 shrink-0 ml-auto"
              data-testid={`category-chip-progress-${node.name}`}
              title={`${p.done}/${p.total} tamamlandı · %${pct}`}
            >
              ({p.done}/{p.total})
              <span className="inline-block w-6 h-1 rounded-full bg-sertex-cyan/15 overflow-hidden align-middle">
                <span className="block h-full bg-emerald-400/80" style={{ width: `${pct}%` }} />
              </span>
            </span>
          )}
        </button>
        {!detached && (
          <button
            onClick={() => openDetachedPanel({ category: node.id, categoryName: node.name })}
            data-testid={`category-detach-${node.name}`}
            title={`"${node.name}" iş kolunu ayrı pencerede aç`}
            className="px-1.5 self-stretch flex items-center border-l border-sertex-cyan/20 text-sertex-textMuted hover:text-sertex-cyan transition-colors shrink-0"
          >
            <ExternalLink className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    );
    return (
      <div key={node.id}>
        <SortableCategoryRow id={node.id} index={Math.max(0, visibleCatIds.indexOf(node.id))}>
          {(handleRef) => (panelId ? placeholderInner() : rowInner(handleRef))}
        </SortableCategoryRow>
        {!panelId && hasKids && isOpen && node.children.map((ch) => renderFilterNode(ch, depth + 1))}
      </div>
    );
  };


  // GÖREV BAĞLAMA — Personel Görevleri (team) için grup-farkındalıklı satırlar
  // (statik, sürükleme yok — bu görünüm zaten salt-okunur).
  const teamRows = useMemo(() => {
    const rows = [];
    const byGid = {};
    for (const t of filtered) {
      const gid = t.group_id && groupById[t.group_id] ? t.group_id : null;
      if (gid) {
        let row = byGid[gid];
        if (!row) {
          row = { key: `g:${gid}`, type: "group", group: groupById[gid], tasks: [] };
          byGid[gid] = row;
          rows.push(row);
        }
        row.tasks.push(t);
      } else {
        rows.push({ key: `t:${t.id}`, type: "task", task: t });
      }
    }
    return rows.map((r) =>
      r.type === "group" && r.tasks.length < 2 && r.tasks[0]
        ? { key: `t:${r.tasks[0].id}`, type: "task", task: r.tasks[0] }
        : r
    );
  }, [filtered, groupById]);
  const renderStaticMember = (t) =>
    detachedIds.has(t.id) ? (
      <DetachedPlaceholderCard
        key={t.id}
        task={t}
        displayNumber={numById[t.id]}
        onDock={() => toggleDetach(t.id)}
      />
    ) : (
      <TaskCard
        key={t.id}
        {...cardPropsFor(t)}
        onNudge={() => nudgeTask(t.id, t)}
        nudgeCount={nudgeCounts[t.id] || 0}
      />
    );
  const allVisibleCollapsed =
    visibleTaskIds.length > 0 && visibleTaskIds.every((id) => collapsedIds.has(id));
  const toggleAllCollapsed = () => setAllCollapsed(visibleTaskIds, !allVisibleCollapsed);

  const [showExportSelect, setShowExportSelect] = useState(false);
  const bulkExport = async (kind) => {
    const list = visibleTaskList;
    if (!list.length) {
      toast.error("Dışa aktarılacak görev yok");
      return;
    }
    const catMap = Object.fromEntries((categories || []).map((c) => [c.id, c.name]));
    const heading = `Görev Listesi (${list.length})`;
    try {
      if (kind === "print") printTasks(list, catMap, { heading, showTime: exportShowTime });
      else if (kind === "excel") exportTasksExcel(list, catMap, { showTime: exportShowTime });
      else if (kind === "word") await exportTasksWord(list, catMap, { heading, showTime: exportShowTime });
      toast.success(`${list.length} görev dışa aktarıldı`);
    } catch (e) {
      console.error("[TasksPanel] toplu dışa aktarma hatası:", e);
      toast.error(
        e?.message === "popup-blocked"
          ? "Yazdırma penceresi engellendi — tarayıcıda açılır pencerelere izin verin"
          : "Dışa aktarılamadı"
      );
    }
  };

  return (
    <div className="space-y-3" data-testid="tasks-panel">
      {smartDelete.dialogElement}
      {!detached && (
        <div className="flex justify-end -mb-1" data-testid="tasks-panel-detach-bar">
          <button
            onClick={() =>
              openDetachedPanel({
                category:
                  categoryFilter && categoryFilter !== "__none__" ? categoryFilter : null,
                categoryName:
                  categoryFilter && categoryFilter !== "__none__"
                    ? categories.find((c) => c.id === categoryFilter)?.name || "İŞ KOLU"
                    : "GÖREVLER",
              })
            }
            data-testid="detach-panel-btn"
            title="Görevler panelini (veya seçili iş kolunu) ayrı pencerede aç"
            className="p-1 px-2 rounded border border-sertex-cyan/30 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/60 transition-colors flex items-center gap-1 hud-text"
          >
            <ExternalLink className="h-3 w-3" /> DIŞARI TAŞI
          </button>
        </div>
      )}
      {/* Görevler sekmesi — sadece admin/müdür (isTeamView) için */}
      {isTeamView && (
        <div className="space-y-2" data-testid="task-scope-tabs">
          <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg border border-sertex-cyan/25 bg-sertex-surface/40">
            {[
              { key: "mine", label: "Benim Görevlerim", icon: User },
              { key: "team", label: "Personel Görevleri", icon: Users },
            ].map((tab) => {
              const active = taskScope === tab.key;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setTaskScope(tab.key);
                    setShowArchived(false);
                    setShowAddForm(false);
                  }}
                  data-testid={`task-scope-${tab.key}`}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-md hud-text transition-colors ${
                    active
                      ? "bg-sertex-cyan/20 text-sertex-cyan border border-sertex-cyan/50"
                      : "text-sertex-textMuted hover:text-sertex-text"
                  }`}
                >
                  <TabIcon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
          {taskScope === "team" && (
            <div className="space-y-2">
              {teamSummary && teamSummary.people > 0 && (
                <div
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 hud-text px-1"
                  data-testid="team-summary-strip"
                >
                  <span className="text-sertex-textMuted">
                    <Users className="inline h-3 w-3 mr-1 -mt-0.5" />
                    {teamSummary.people} personel
                  </span>
                  <span className={teamSummary.overdue > 0 ? "text-rose-300" : "text-sertex-textMuted/60"}>
                    <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5" />
                    {teamSummary.overdue} geciken
                  </span>
                  <span className="text-sertex-cyan">{teamSummary.active} aktif</span>
                  <span className="text-emerald-300/90">{teamSummary.done} bitti</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5" data-testid="task-person-filter-wrap">
                <div className="w-full">
                  <PersonFilterSelect
                    owners={teamOwners}
                    value={personFilter}
                    onChange={setPersonFilter}
                    totalCount={tasks.length}
                  />
                </div>
                {teamOwners.length === 0 && (
                  <span className="hud-text text-sertex-textMuted px-1 py-1">
                    Personelinize ait görev bulunmuyor.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {!showArchived && (() => {
        const STAT_META = {
          aktif: { key: "aktif", label: "AKTİF", value: stats.pending, color: "text-sertex-cyan", ring: "ring-sertex-cyan" },
          gecti: { key: "gecti", label: "GEÇTİ", value: stats.overdue, color: "text-rose-300", ring: "ring-rose-400" },
          bekliyor: { key: "bekliyor", label: "BEKLİYOR", value: stats.paused, color: "text-yellow-300", ring: "ring-yellow-400" },
          bitti: { key: "bitti", label: "BİTTİ", value: stats.done, color: "text-emerald-300", ring: "ring-emerald-400" },
        };
        return (
          <Reorder.Group
            axis="x"
            values={filterOrder}
            onReorder={persistFilterOrder}
            as="div"
            className="grid grid-cols-4 gap-1.5"
            data-testid="task-stat-reorder-group"
          >
            {filterOrder.map((key) => {
              const s = STAT_META[key];
              if (!s) return null;
              return (
                <DraggableStat
                  key={s.key}
                  stat={s}
                  active={filters.includes(s.key)}
                  onToggle={() => toggleFilter(s.key)}
                />
              );
            })}
          </Reorder.Group>
        );
      })()}
      {showArchived && (() => {
        const ARCH_META = [
          { key: "done", label: "BİTMİŞ", value: archiveCounts.done, color: "text-emerald-300", ring: "ring-emerald-400", border: "border-emerald-400" },
          { key: "cancelled", label: "İPTAL", value: archiveCounts.cancelled, color: "text-amber-300", ring: "ring-amber-400", border: "border-amber-400" },
          { key: "deleted", label: "SİLİNMİŞ", value: archiveCounts.deleted, color: "text-rose-300", ring: "ring-rose-400", border: "border-rose-400" },
        ];
        return (
          <div className="flex flex-col gap-1.5">
            <div className="grid grid-cols-3 gap-1.5" data-testid="archive-group-chips">
              {ARCH_META.map((a) => {
                const active = archiveGroup === a.key;
                return (
                  <button
                    key={a.key}
                    onClick={() => setArchiveGroup(a.key)}
                    data-testid={`archive-group-${a.key}`}
                    className={`glass-panel rounded-md px-2 py-1.5 flex flex-col items-center justify-center transition-all border ${
                      active ? `${a.border} ring-1 ${a.ring} bg-white/5` : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <span className={`text-lg font-bold leading-none ${a.color}`}>{a.value}</span>
                    <span className="hud-text text-[10px] mt-0.5 text-sertex-textMuted">{a.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1" data-testid="archive-sort">
                {[
                  { k: "new", label: "Yeni" },
                  { k: "old", label: "Eski" },
                  { k: "az", label: "A-Z" },
                ].map((s) => (
                  <button
                    key={s.k}
                    onClick={() => setArchiveSort(s.k)}
                    data-testid={`archive-sort-${s.k}`}
                    className={`px-2 py-0.5 rounded border text-[10px] font-mono hud-text transition-colors ${
                      archiveSort === s.k
                        ? "border-sertex-cyan text-sertex-cyan bg-sertex-cyan/10"
                        : "border-white/10 text-sertex-textMuted hover:border-white/25"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {archiveGroup === "cancelled" && caps.perm_delete && archiveCounts.cancelled > 0 && (
                <div className="flex items-center gap-1.5" data-testid="archive-cancelled-bulk-bar">
                  <button
                    onClick={() => setCancelSel((prev) => (prev.size === sorted.length ? new Set() : new Set(sorted.map((t) => t.id))))}
                    data-testid="archive-cancelled-select-all"
                    className="px-2 py-1 rounded border border-white/15 text-sertex-textSecondary hover:border-sertex-cyan/40 hover:text-sertex-cyan hud-text transition-colors"
                  >
                    {cancelSel.size === sorted.length && sorted.length > 0 ? "SEÇİMİ KALDIR" : "TÜMÜNÜ SEÇ"}
                  </button>
                  <button
                    onClick={() => runCancelledBulkDelete({ all: false })}
                    disabled={cancelSel.size === 0}
                    data-testid="archive-cancelled-delete-selected"
                    className="flex items-center gap-1 px-2 py-1 rounded border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 hud-text transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-3 w-3" /> SEÇİLENLERİ KALICI SİL ({cancelSel.size})
                  </button>
                  <button
                    onClick={() => runCancelledBulkDelete({ all: true })}
                    data-testid="archive-cancelled-delete-all"
                    className="flex items-center gap-1 px-2 py-1 rounded border border-rose-500/50 text-rose-200 hover:bg-rose-500/15 hud-text transition-colors"
                  >
                    <Trash2 className="h-3 w-3" /> TÜMÜNÜ KALICI SİL
                  </button>
                </div>
              )}
              {archiveGroup === "deleted" && caps.empty_trash && archiveCounts.deleted > 0 && (
                <button
                  onClick={emptyTrash}
                  data-testid="archive-empty-trash"
                  className="flex items-center gap-1 px-2 py-1 rounded border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 hud-text transition-colors"
                >
                  <Trash2 className="h-3 w-3" /> ÇÖP KUTUSUNU BOŞALT
                </button>
              )}
            </div>
          </div>
        );
      })()}
      {!showArchived && filters.length > 0 && (
        <div className="flex items-center justify-between hud-text px-1">
          <span className="text-sertex-textMuted">
            FİLTRE: <span className="text-sertex-cyan">{filters.map((f) => f.toUpperCase()).join(" · ")}</span>
          </span>
          <button
            onClick={clearFilters}
            data-testid="task-clear-filters"
            className="text-sertex-cyan hover:text-sertex-text underline decoration-dotted"
          >
            Temizle
          </button>
        </div>
      )}

      {/* Yedek sağlık kartı KİŞİSEL veri hijyenidir — role/ekip görünümüne
          bağlanmaz. `!showArchived` kalıyor: arşiv görünümü geçmiş kayıtlara
          bakılan ayrı bir bağlamdır ve orada yedek uyarısı gürültü olur. */}
      {!showArchived && <BackupHealthCard />}

      {/* Görev arama — binlerce görev içinde hızlı bulma */}
      <div className="flex items-start gap-2">
        <div className="relative flex-1 min-w-0" data-testid="task-search-bar">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-sertex-textMuted pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Görev ara… (başlık, açıklama, kişi, şirket, iş kolu)"
            {...noAutofill("task")}
            ref={taskSearchRef}
            data-testid="task-search-input"
            className="w-full bg-sertex-surface border border-sertex-cyan/25 rounded-md pl-8 pr-8 py-1.5 text-xs font-mono text-sertex-text placeholder:text-sertex-textMuted/60 focus:border-sertex-cyan outline-none transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(""); setSearchTerm(""); }}
              data-testid="task-search-clear"
              title="Aramayı temizle"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sertex-textMuted hover:text-sertex-cyan transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* FAZ C — Pencere düzenleri (yalnızca ANA panelde; kol penceresinde değil) */}
        {!detached && (
          <div className="shrink-0 pt-0.5 flex items-center gap-1">
            <UndoMenu onUndone={load} />
            <CollectAllButton />
            <LayoutsMenu
              collectWindows={collectLayoutWindows}
              applyLayout={applyLayout}
              openWindowCount={openWindowCount}
            />
          </div>
        )}
      </div>
      {searchActive && (
        <div className="hud-text text-[10px] text-sertex-cyan/70 -mt-1 px-1" data-testid="task-search-count">
          {filtered.length} sonuç bulundu
        </div>
      )}
      {searchActive && !showArchived && archiveSearchResults.length > 0 && (
        <div className="glass-panel rounded-md border border-white/10 p-2 space-y-1" data-testid="archive-search-results">
          <div className="hud-text text-[10px] text-sertex-textMuted flex items-center gap-1.5">
            <Archive className="h-3 w-3" /> ARŞİVDE BULUNANLAR ({archiveSearchResults.length})
          </div>
          {archiveSearchResults.slice(0, 20).map((t) => {
            const g = t.deleted ? "deleted" : t.cancelled ? "cancelled" : "done";
            const meta = { done: { label: "BİTMİŞ", cls: "text-emerald-300 border-emerald-400/40" }, cancelled: { label: "İPTAL", cls: "text-amber-300 border-amber-400/40" }, deleted: { label: "SİLİNMİŞ", cls: "text-rose-300 border-rose-400/40" } }[g];
            return (
              <button
                key={t.id}
                onClick={() => { setShowArchived(true); setArchiveGroup(g); }}
                data-testid={`archive-search-row-${t.id}`}
                className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5 transition-colors text-left"
              >
                <span className={`shrink-0 text-[9px] font-mono px-1.5 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                <span className="flex-1 min-w-0 truncate text-xs font-mono text-sertex-text">
                  {t.title}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Şablondan Başla çubuğu + Şablon Kütüphanesi girişi */}
      {!showArchived && (
        <TemplateBar
          refreshKey={templatesRefresh}
          onUse={handleUseTemplate}
          onManage={() => setTemplatesOpen(true)}
        />
      )}

      {/* Görev Kopyalama panosu — dolu iken göster (iş koluna sağ tık → Yapıştır) */}
      {clipboard?.sourceId && !showArchived && (
        <div
          data-testid="task-clipboard-bar"
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-sertex-cyan/40 bg-sertex-cyan/5"
        >
          <ClipboardPaste className="h-3.5 w-3.5 text-sertex-cyan shrink-0" />
          <span className="hud-text text-sertex-cyan truncate flex-1">
            Kopyalandı: {clipboard.title}
          </span>
          <button
            onClick={() => updateTaskClipboard({ includeSubtasks: !clipboard.includeSubtasks })}
            data-testid="task-clipboard-toggle-subtasks"
            title="Alt görevleri de kopyala"
            className={`hud-text flex items-center gap-1 px-1.5 py-0.5 rounded border shrink-0 ${clipboard.includeSubtasks ? "border-sertex-cyan/60 bg-sertex-cyan/15 text-sertex-cyan" : "border-white/15 text-sertex-textMuted"}`}
          >
            <span className={`h-2.5 w-2.5 rounded-sm border ${clipboard.includeSubtasks ? "bg-sertex-cyan border-sertex-cyan" : "border-white/40"}`} />
            Alt görevler
          </button>
          <button
            onClick={() => updateTaskClipboard({ includeAttachments: !clipboard.includeAttachments })}
            data-testid="task-clipboard-toggle-attachments"
            title="Dosyaları da kopyala"
            className={`hud-text flex items-center gap-1 px-1.5 py-0.5 rounded border shrink-0 ${clipboard.includeAttachments ? "border-sertex-cyan/60 bg-sertex-cyan/15 text-sertex-cyan" : "border-white/15 text-sertex-textMuted"}`}
          >
            <span className={`h-2.5 w-2.5 rounded-sm border ${clipboard.includeAttachments ? "bg-sertex-cyan border-sertex-cyan" : "border-white/40"}`} />
            Dosyalar
          </button>
          <span className="hud-text text-sertex-textMuted/70 text-[10px] hidden md:inline shrink-0">
            iş koluna sağ tıkla → Yapıştır
          </span>
          <button
            onClick={() => clearTaskClipboard()}
            data-testid="task-clipboard-clear"
            className="hud-text text-rose-300 hover:text-rose-200 flex items-center gap-1 shrink-0"
          >
            <X className="h-3 w-3" /> Panoyu Temizle
          </button>
        </div>
      )}

      {taskBulk.selectMode && (
        <TaskBulkBar
          count={taskBulk.ids.length}
          testPrefix="task-bulk"
          categories={categories}
          tasks={tasks}
          ids={taskBulk.ids}
          onRefresh={load}
          onSelectAll={() => taskBulk.selectAll(visibleTaskIds)}
          onClear={taskBulk.clear}
          onCancel={taskBulk.exit}
          onAction={taskBulk.runAction}
        />
      )}

      {/* İş Kolu FİLTRE ağacı — tıklanabilir, açılır-kapanır liste (ana kol → alt kol) */}
      {categories.length > 0 && !showArchived && (
        <div className="flex flex-col gap-1" data-testid="category-filter-bar">
          {expandableFilterIds.length > 0 && (
            <button
              onClick={toggleAllFilterNodes}
              data-testid="catfilter-toggle-all"
              title={allFilterOpen ? "Tüm alt kolları gizle" : "Tüm alt kolları göster"}
              className="self-end flex items-center gap-1 px-2 py-0.5 rounded border border-sertex-cyan/25 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/50 hud-text transition-colors"
            >
              {allFilterOpen ? <ChevronsDownUp className="h-3 w-3" /> : <ChevronsUpDown className="h-3 w-3" />}
              {allFilterOpen ? "HEPSİNİ KAPAT" : "HEPSİNİ AÇ"}
            </button>
          )}
          <button
            onClick={() => setCategoryFilter("")}
            data-testid="category-chip-all"
            className={`px-2 py-1 rounded hud-text border transition-colors flex items-center gap-1 ${
              categoryFilter === ""
                ? "border-sertex-cyan text-sertex-cyan bg-sertex-cyan/10"
                : "border-sertex-cyan/25 text-sertex-textMuted hover:text-sertex-cyan"
            }`}
          >
            TÜMÜ {categoryCounts && <span className="opacity-70">({categoryCounts.__all__.done}/{categoryCounts.__all__.total})</span>}
          </button>
          <CategoryDndProvider
            key={catDndKey}
            ids={visibleCatIds}
            onCatReorder={handleCatDndReorder}
            onCatDropOutside={handleCatDropOutside}
          >
            {catForest.map((node) => renderFilterNode(node, 0))}
          </CategoryDndProvider>
          <button
            onClick={() => setCategoryFilter("__none__")}
            onContextMenu={(e) => {
              if (!clipboard?.sourceId) return;
              e.preventDefault();
              setPasteMenu({ x: e.clientX, y: e.clientY, categoryId: null, categoryName: "Kolsuz" });
            }}
            data-testid="category-chip-none"
            data-cat-drop="__none__"
            title="Görevi buraya bırakınca iş kolundan çıkarılır"
            className={`px-2 py-1 rounded hud-text border transition-colors flex items-center gap-1 ${
              taskDragOverCatId === "__none__" ? "ring-2 ring-sertex-cyan/80 bg-sertex-cyan/10" : ""
            } ${
              categoryFilter === "__none__"
                ? "border-sertex-textMuted text-sertex-textMuted bg-sertex-textMuted/10"
                : "border-sertex-cyan/25 text-sertex-textMuted hover:text-sertex-cyan"
            }`}
          >
            KOLSUZ {categoryCounts && <span className="opacity-70">({categoryCounts.__none__.done}/{categoryCounts.__none__.total})</span>}
          </button>
        </div>
      )}

      {!showAddForm ? (
        <div className="flex items-center gap-2">
          {!showArchived && taskScope !== "team" && (
            <button
              onClick={() => setShowAddForm(true)}
              data-testid="task-add-toggle"
              className="flex-1 py-2 border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/10 rounded-md hud-text flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> YENİ GÖREV
            </button>
          )}
          <button
            onClick={() => setShowArchived((v) => !v)}
            data-testid="archive-toggle"
            title={showArchived ? "Aktif görevlere dön" : "Arşivi göster"}
            className={`${showArchived ? "flex-1" : "shrink-0"} py-2 px-3 border rounded-md hud-text flex items-center justify-center gap-2 transition-colors ${
              showArchived
                ? "border-sertex-cyan text-sertex-bg bg-sertex-cyan hover:bg-sertex-cyan/90"
                : "border-sertex-cyan/30 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/60"
            }`}
          >
            {showArchived ? (
              <>
                <ArchiveRestore className="h-3.5 w-3.5" /> AKTİF'E DÖN
              </>
            ) : (
              <>
                <Archive className="h-3.5 w-3.5" />
                <span>ARŞİV</span>
                {archivedCount > 0 && (
                  <span className="ml-1 px-1.5 rounded-full bg-sertex-cyan/20 text-sertex-cyan text-[10px]">
                    {archivedCount}
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-2 glass-panel corner-bracket p-2 relative">
          <button
            onClick={closeAddForm}
            data-testid="task-add-close"
            className="absolute top-1 right-1 p-0.5 text-sertex-textMuted hover:text-sertex-cyan"
          >
            <X className="h-3 w-3" />
          </button>
          {addTeklif && (
            <DraftRestoreBar
              at={addTeklif.at}
              onRestore={() => addTaslakGeriYukle(addTeklif.veri)}
              onIgnore={() => { addTaslakSil(); toast("Taslak yoksayıldı"); }}
              testid="detayli-draft-bar"
            />
          )}
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Görev başlığı"
            autoFocus
            data-testid="task-title-input"
            className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Açıklama (opsiyonel)"
            rows={2}
            data-testid="task-desc-input"
            className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none resize-none"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">BAŞLANGIÇ</div>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="task-startdate-input"
                className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text focus:border-sertex-cyan outline-none"
              />
            </div>
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">BİTİŞ</div>
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="task-duedate-input"
                className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text focus:border-sertex-cyan outline-none"
              />
            </div>
          </div>
          <div className="space-y-2" style={{ display: isTeamView ? undefined : "none" }}>
            {teamMembers.length > 0 ? (
              <MultiAssigneeSelect
                members={teamMembers}
                selfUser={user ? { id: user.id, username: user.username } : null}
                selectedIds={assigneeUserIds}
                companyFilter={companyName}
                onChange={(ids) => {
                  setAssigneeUserIds(ids);
                  // Auto-fill company from the single OTHER selected assignee
                  // (ignore self). Skip when multiple others are chosen.
                  const others = ids.filter((x) => x !== user?.id);
                  if (companyAutoFilled || !companyName.trim()) {
                    if (others.length === 1) {
                      setCompanyName(teamMembers.find((m) => m.id === others[0])?.company_name || "");
                      setCompanyAutoFilled(true);
                    } else if (ids.length === 0) {
                      setCompanyName("");
                      setCompanyAutoFilled(true);
                    }
                  }
                }}
              />
            ) : (
              <input
                type="text"
                value={assigneeName}
                onChange={(e) => setAssigneeName(e.target.value)}
                placeholder="Görev sahibi (opsiyonel)"
                data-testid="task-assignee-input"
                className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none"
              />
            )}
            {/* Faz 9 CP8.5 — Company field: custom dark-themed combobox. */}
            <CompanyCombobox
              value={companyName}
              onChange={setCompanyName}
              onManualEdit={(isManual) => setCompanyAutoFilled(!isManual)}
              options={teamMembers.map((m) => m.company_name).filter(Boolean)}
              placeholder="Şirket (opsiyonel)"
              testId="task-company-input"
            />
          </div>
          {categories.length > 0 && (
            <CategorySelect
              categories={categories}
              value={newCategoryId}
              onChange={setNewCategoryId}
              testId="task-category-select"
            />
          )}
          {/* FAZ D — taslak alt görevler (görev oluşunca hiyerarşiyle kaydedilir) */}
          <PendingSubtasks apiRef={subsRef} />
          {/* Faz 8 CP5 — Yaklaşan-son-tarih uyarı ayarı. Görev bazlı override.
              Note: option children must be plain strings; we precompute the
              dynamic default label to avoid <span> injection inside <option>. */}
          <div className="flex items-center gap-2">
            {(() => {
              const defaultLabel = reminderConfig?.effective
                ? `⏱ Uyarı: Varsayılan (${reminderConfig.effective} gün)`
                : "⏱ Uyarı: Varsayılan";
              return (
                <select
                  value={newReminderDisabled ? "__off__" : (newReminderDays == null ? "" : String(newReminderDays))}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__off__") {
                      setNewReminderDisabled(true);
                      setNewReminderDays(null);
                    } else if (v === "") {
                      setNewReminderDisabled(false);
                      setNewReminderDays(null);
                    } else {
                      setNewReminderDisabled(false);
                      setNewReminderDays(parseInt(v, 10));
                    }
                  }}
                  data-testid="task-reminder-days-select"
                  className="flex-1 bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text focus:border-sertex-cyan outline-none"
                >
                  <option value="">{defaultLabel}</option>
                  {REMINDER_DAY_CHOICES.map((d) => (
                    <option key={d} value={d}>{`⏱ Uyarı: ${d} gün önce`}</option>
                  ))}
                  <option value="__off__">🚫 Bu görev için hatırlatıcı kapalı</option>
                </select>
              );
            })()}
          </div>
          <RecurringReminderFields
            value={newReminder}
            onChange={setNewReminder}
            testPrefix="new-task-reminder"
          />
          <SerialDateFields
            testPrefix="task"
            assignSerial={newAssignSerial}
            setAssignSerial={setNewAssignSerial}
            showDate={newShowDate}
            setShowDate={setNewShowDate}
          />
          {/* 📎 Görev oluşturulurken dosya ekle (görev oluşunca yüklenir) */}
          <PendingAttachments files={pendingFiles} onChange={setPendingFiles} />
          {failedSubtasks && (
            <div
              data-testid="pending-subtask-failed"
              className="rounded border border-rose-400/50 bg-rose-500/10 px-2 py-1.5 space-y-1"
            >
              <div className="hud-text text-rose-300">
                {failedSubtasks.length} alt görev oluşturulamadı — verileriniz duruyor:
              </div>
              <div className="text-[11px] font-mono text-rose-200/90 max-h-20 overflow-y-auto">
                {failedSubtasks.map((t, i) => (
                  <div key={i} className="truncate">• {t}</div>
                ))}
              </div>
              <button
                type="button"
                onClick={retrySubtasks}
                data-testid="pending-subtask-retry"
                className="px-2 py-0.5 rounded border border-rose-400/50 text-rose-200 hover:bg-rose-500/20 hud-text transition-colors"
              >
                TEKRAR DENE
              </button>
            </div>
          )}
          <button
            onClick={addTask}
            disabled={ekleniyor}
            data-testid="task-add-submit"
            className="w-full py-1.5 bg-sertex-cyan/20 border border-sertex-cyan text-sertex-cyan hover:bg-sertex-cyan hover:text-sertex-bg rounded-md hud-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {ekleniyor ? "EKLENİYOR…" : "EKLE"}
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="flex items-center justify-end gap-1.5" data-testid="task-bulk-export-toolbar">
          {taskScope !== "team" && (
            <button
              onClick={() => setLinkModal({ mode: "create" })}
              data-testid="link-tasks-toolbar-btn"
              title="Görevleri birbirine bağla (grup oluştur)"
              className="mr-auto flex items-center gap-1.5 px-2 py-1 rounded-md border border-sertex-cyan/40 text-sertex-cyan/90 hover:text-sertex-cyan hover:border-sertex-cyan hover:bg-sertex-cyan/10 hud-text transition-colors"
            >
              <Link2 className="h-3.5 w-3.5" /> BAĞLA
            </button>
          )}
          <span className="hud-text text-sertex-textMuted mr-0.5">DIŞA AKTAR ({filtered.length}):</span>
          <button
            onClick={() => setShowExportSelect(true)}
            data-testid="bulk-export-select"
            title="Görev seçip yazdır / dışa aktar"
            className="p-1.5 rounded-md border border-sertex-cyan/40 text-sertex-cyan/90 hover:text-sertex-cyan hover:border-sertex-cyan/70 transition-colors"
          >
            <ListChecks className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => bulkExport("print")}
            data-testid="bulk-export-print"
            title="Tümünü Yazdır / PDF"
            className="p-1.5 rounded-md border border-sertex-cyan/30 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/60 transition-colors"
          >
            <Printer className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => bulkExport("excel")}
            data-testid="bulk-export-excel"
            title="Tümünü Excel (.xlsx)"
            className="p-1.5 rounded-md border border-emerald-500/30 text-emerald-300/80 hover:text-emerald-300 hover:border-emerald-500/60 transition-colors"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => bulkExport("word")}
            data-testid="bulk-export-word"
            title="Tümünü Word (.docx)"
            className="p-1.5 rounded-md border border-blue-500/30 text-blue-300/80 hover:text-blue-300 hover:border-blue-500/60 transition-colors"
          >
            <FileText className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {showExportSelect && (
        <ExportSelectModal
          tasks={visibleTaskList}
          categories={categories}
          onClose={() => setShowExportSelect(false)}
        />
      )}

      {visibleTaskIds.length > 0 && (
        <div className="flex items-center justify-end gap-2" data-testid="task-collapse-toolbar">
          {showArchived && categories.length > 0 && (
            <button
              onClick={() => setArchiveByCategory((v) => !v)}
              data-testid="archive-groupby-toggle"
              title={archiveByCategory ? "İş kolu gruplamayı kaldır (düz liste)" : "Biten görevleri iş koluna göre grupla"}
              className={`mr-auto flex items-center gap-1 px-2 py-1 rounded-md border hud-text transition-colors ${
                archiveByCategory
                  ? "border-sertex-cyan text-sertex-cyan bg-sertex-cyan/10"
                  : "border-sertex-cyan/30 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/60"
              }`}
            >
              <Tag className="h-3.5 w-3.5" />
              {archiveByCategory ? "GRUPLAMAYI KALDIR" : "İŞ KOLUNA GÖRE GRUPLA"}
            </button>
          )}
          {hasHierCats && (
            <button
              onClick={toggleAllCatTrees}
              data-testid="cattree-toggle-all"
              title={allTreesOpen ? "Tüm iş kolu ağaçlarını küçült" : "Tüm iş kolu ağaçlarını aç (ana kol → alt kol)"}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-sertex-cyan/30 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/60 hud-text transition-colors"
            >
              <Tag className="h-3.5 w-3.5" />
              {allTreesOpen ? "İŞ KOLU AĞAÇLARINI KAPAT" : "İŞ KOLU AĞAÇLARINI AÇ"}
            </button>
          )}
          <button
            onClick={toggleAllCollapsed}
            data-testid="task-collapse-all"
            title={allVisibleCollapsed ? "Tüm görevleri büyüt" : "Tüm görevleri küçült"}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-sertex-cyan/30 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/60 hud-text transition-colors"
          >
            {allVisibleCollapsed ? (
              <ChevronsUpDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronsDownUp className="h-3.5 w-3.5" />
            )}
            {allVisibleCollapsed ? "TÜMÜNÜ BÜYÜT" : "TÜMÜNÜ KÜÇÜLT"}
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="hud-text text-sertex-textMuted text-center py-8" data-testid="task-empty-state">
          {searchActive
            ? `"${searchTerm.trim()}" için görev bulunamadı.`
            : showArchived
            ? "Arşivde henüz görev yok."
            : taskScope === "team"
            ? (personFilter === "__none__"
                ? "Görüntülemek için bir personel seçin veya 'Tümü'nü işaretleyin."
                : personFilter
                ? "Bu kişiye ait görev yok."
                : "Personelinize ait görev bulunmuyor.")
            : filters.length > 0
            ? "Bu filtreye uygun görev yok."
            : "Henüz görev yok. Yukarıdaki butonla ekle."}
        </div>
      ) : taskScope === "team" ? (
        // Personel Görevleri — read-oriented list (no drag reorder across
        // people). Owner label shown via isTeamView on the card. Bağlı
        // görevler statik grup bloğu olarak gösterilir (sürükleme yok).
        <div className="space-y-2" data-testid="team-task-list">
          {teamRows.map((row) =>
            row.type === "group" ? (
              <StaticTaskGroupBlock
                key={row.key}
                group={row.group}
                tasks={row.tasks}
                renderStaticMember={renderStaticMember}
                onEditGroup={(g) => setLinkModal({ mode: "edit", groupId: g.id })}
                onDissolve={dissolveGroup}
              />
            ) : (
              renderStaticMember(row.task)
            )
          )}
        </div>
      ) : showArchived && archiveByCategory && !searchActive ? (
        (() => {
          // Arşivi iş koluna (kategori) göre grupla — statik kartlar, düz liste yerine.
          const buckets = new Map();
          for (const t of sorted) {
            const key = t.category_id || "__none__";
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push(t);
          }
          const catNameOf = (id) => categories.find((c) => c.id === id)?.name || null;
          // İş kolu ağacı sırası + derinlik (alt kollar ana kolun hemen altında, girintili).
          const flatCats = flattenTree(categories);
          const depthOf = new Map(flatCats.map((c) => [c.id, c.__depth || 0]));
          const orderOf = new Map(flatCats.map((c, i) => [c.id, i]));
          const entries = [...buckets.entries()].map(([key, tasks]) => ({
            key,
            name: key === "__none__" ? "Kolsuz" : (catNameOf(key) || "Bilinmeyen İş Kolu"),
            depth: key === "__none__" ? 0 : (depthOf.get(key) || 0),
            order: key === "__none__" ? Number.POSITIVE_INFINITY : (orderOf.has(key) ? orderOf.get(key) : Number.POSITIVE_INFINITY),
            tasks,
          }));
          entries.sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order;
            if (a.key === "__none__") return 1;
            if (b.key === "__none__") return -1;
            return a.name.localeCompare(b.name, "tr");
          });
          const q = archiveCatQuery.trim().toLocaleLowerCase("tr");
          const shown = q ? entries.filter((g) => g.name.toLocaleLowerCase("tr").includes(q)) : entries;
          const allCollapsed = shown.length > 0 && shown.every((g) => collapsedArchiveCats.has(g.key));
          return (
            <div className="space-y-3" data-testid="archive-grouped-list">
              {entries.length > 1 && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="h-3.5 w-3.5 text-sertex-textMuted absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      value={archiveCatQuery}
                      onChange={(e) => setArchiveCatQuery(e.target.value)}
                      data-testid="archive-cat-search"
                      placeholder="İş kolu ara..."
                      {...noAutofill("cat")}
                      className="w-full pl-8 pr-3 py-1.5 rounded-md bg-sertex-surface/60 border border-sertex-cyan/25 text-sertex-text font-mono text-xs placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setCollapsedArchiveCats(allCollapsed ? new Set() : new Set(shown.map((g) => g.key)))}
                    data-testid="archive-cats-toggle-all"
                    title={allCollapsed ? "Tüm iş kollarını aç" : "Tüm iş kollarını kapat"}
                    className="flex items-center gap-1 px-2 py-1 rounded-md border border-sertex-cyan/30 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/60 hud-text transition-colors shrink-0"
                  >
                    {allCollapsed ? <ChevronsUpDown className="h-3.5 w-3.5" /> : <ChevronsDownUp className="h-3.5 w-3.5" />}
                    {allCollapsed ? "HEPSİNİ AÇ" : "HEPSİNİ KAPAT"}
                  </button>
                </div>
              )}
              {shown.length === 0 ? (
                <div className="hud-text text-sertex-textMuted py-6 text-center" data-testid="archive-cat-nomatch">Eşleşen iş kolu yok</div>
              ) : shown.map((g) => {
                const isCollapsed = collapsedArchiveCats.has(g.key) && !q;
                const gq = (archiveGroupQuery[g.key] || "").trim().toLocaleLowerCase("tr");
                const gTasks = gq
                  ? g.tasks.filter(
                      (t) =>
                        (t.title || "").toLocaleLowerCase("tr").includes(gq) ||
                        (t.description || "").toLocaleLowerCase("tr").includes(gq),
                    )
                  : g.tasks;
                return (
                  <div
                    key={g.key}
                    data-testid={`archive-cat-group-${g.key}`}
                    style={{ marginLeft: g.depth * 16 }}
                    className={g.depth > 0 ? "border-l-2 border-sertex-cyan/40 pl-2" : ""}
                  >
                    <button
                      type="button"
                      onClick={() => toggleArchiveCat(g.key)}
                      data-testid={`archive-cat-toggle-${g.key}`}
                      className="w-full flex items-center gap-2 mb-1.5 px-1 group/cat"
                      title={isCollapsed ? "Aç" : "Kapat"}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-3.5 w-3.5 text-sertex-cyan/70" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-sertex-cyan/70" />
                      )}
                      <Tag className="h-3.5 w-3.5 text-sertex-cyan/70" />
                      <span className="hud-text text-sertex-cyan group-hover/cat:text-sertex-text transition-colors">{g.name}</span>
                      <span className="hud-text text-[10px] text-sertex-textMuted">({g.tasks.length})</span>
                      <div className="flex-1 h-px bg-sertex-cyan/15" />
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-2 mb-2">
                        {g.tasks.length > 1 && (
                          <div className="relative">
                            <Search className="h-3.5 w-3.5 text-sertex-textMuted absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                              value={archiveGroupQuery[g.key] || ""}
                              onChange={(e) => setArchiveGroupQuery((p) => ({ ...p, [g.key]: e.target.value }))}
                              data-testid={`archive-group-search-${g.key}`}
                              placeholder={`${g.name} içinde görev ara...`}
                              className="w-full pl-8 pr-3 py-1.5 rounded-md bg-sertex-surface/40 border border-sertex-cyan/20 text-sertex-text font-mono text-xs placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none"
                            />
                          </div>
                        )}
                        {gTasks.length === 0 ? (
                          <div className="hud-text text-sertex-textMuted py-3 text-center" data-testid={`archive-group-nomatch-${g.key}`}>Bu iş kolunda eşleşen görev yok</div>
                        ) : (
                          gTasks.map((t) => renderStaticMember(t))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : (showArchived || (filters.length === 0 && !categoryFilter)) && !searchActive ? (
        <TaskSortableGroup
          values={rowKeys}
          onReorder={handleReorderRows}
          className="space-y-2"
          onCategoryDragMove={(pt) => handleTaskDragToCategory(null, { point: pt })}
          onCategoryDrop={(key, pt) => {
            const r = rowByKey[key];
            const t = r && r.type !== "group" ? r.task : null;
            handleTaskDropCategory(t, null, { point: pt });
          }}
          onDropOutside={(key, pt) => {
            const r = rowByKey[key];
            if (r && r.type !== "group") detachByDrag(r.task.id, pt);
          }}
          onSubtaskReorder={handleSubtaskDrag}
          onSubtaskDropOutside={(subId, _data, pt) => detachSubByDrag(subId, pt)}
          onDropIntoTask={surukleyerekTasi}
          invalidDropIdsFor={gecersizBirakmaIdleri}
          dragCountOf={surukleSayisi}
          kapsam={dndKapsam}
        >
          {(orderedKeys) => orderedKeys.map((key, rowIndex) => {
            const row = rowByKey[key];
            if (!row) return null;
            if (row.type === "group") {
              if (detachedGroupIds.has(row.group.id)) {
                return (
                  <GroupDetachedRow
                    key={key}
                    rowKey={key}
                    index={rowIndex}
                    group={row.group}
                    count={row.tasks.length}
                    onDock={() => toggleGroupDetach(row.group.id)}
                  />
                );
              }
              return (
                <TaskGroupBlock
                  key={key}
                  rowKey={key}
                  index={rowIndex}
                  group={row.group}
                  tasks={row.tasks}
                  renderMember={renderMember}
                  onReorderGroup={handleReorderGroup}
                  onEditGroup={(g) => setLinkModal({ mode: "edit", groupId: g.id })}
                  onDissolve={dissolveGroup}
                  onDetach={(g) => toggleGroupDetach(g.id)}
                />
              );
            }
            const t = row.task;
            const detached = detachedIds.has(t.id);
            return (
              <OuterTaskRow
                key={key}
                rowKey={key}
                index={rowIndex}
                detached={detached}
                placeholderProps={{
                  task: t,
                  displayNumber: numById[t.id],
                  onDock: () => toggleDetach(t.id),
                }}
                cardProps={cardPropsFor(t)}
              />
            );
          }).filter(Boolean)}
        </TaskSortableGroup>
      ) : (
        // Faz 9 CP4.31 — Category filter path now uses Reorder.Group too so
        // the drag handle works when a category filter is active. Reordering
        // within a filter reshuffles only the visible slice; non-visible
        // tasks keep their positions in the master list.
        <TaskSortableGroup
          values={filtered}
          onReorder={handleReorderFiltered}
          className="space-y-2"
          key={tick}
          onCategoryDragMove={(pt) => handleTaskDragToCategory(null, { point: pt })}
          onCategoryDrop={(tid, pt) => {
            const t = filtered.find((x) => x.id === tid) || null;
            handleTaskDropCategory(t, null, { point: pt });
          }}
          onDropOutside={(tid, pt) => detachByDrag(tid, pt)}
          onSubtaskReorder={handleSubtaskDrag}
          onSubtaskDropOutside={(subId, _data, pt) => detachSubByDrag(subId, pt)}
          onDropIntoTask={surukleyerekTasi}
          invalidDropIdsFor={gecersizBirakmaIdleri}
          dragCountOf={surukleSayisi}
          kapsam={dndKapsam}
        >
          {(orderedFiltered) => (() => {
            // Pin-aware numaralama (görev listesiyle aynı kural): sabitlenenler
            // atlanır, sabit görev kendi pinned_number'ını gösterir.
            const reserved = new Set();
            for (const t of orderedFiltered) {
              if (t.status !== "done" && t.number_pinned && t.pinned_number != null) {
                reserved.add(t.pinned_number);
              }
            }
            let counter = 0;
            return orderedFiltered.map((t, cardIndex) => {
              const isDone = t.status === "done";
              let num = null;
              if (!isDone) {
                if (t.number_pinned && t.pinned_number != null) {
                  num = t.pinned_number;
                } else {
                  counter += 1;
                  while (reserved.has(counter)) counter += 1;
                  num = counter;
                }
              }
              if (detachedIds.has(t.id)) {
                return (
                  <ReorderablePlaceholder
                    key={t.id}
                    task={t}
                    index={cardIndex}
                    displayNumber={num}
                    onDock={() => toggleDetach(t.id)}
                  />
                );
              }
              return (
                <ReorderableTaskCard
                  key={t.id}
                  task={t}
                  index={cardIndex}
                  displayNumber={num}
                  collapsed={collapsedIds.has(t.id)}
                  onToggleCollapse={() => toggleCollapse(t.id)}
                  onToggleDetach={() => toggleDetach(t.id)}
                  onStatusChange={(status) => setStatus(t.id, status)}
                  onDelete={() => removeTask(t.id)}
                  onEdit={() => setEditing(t)}
                  onSetReminder={(iso, opts) => setReminder(t.id, iso, opts)}
                  onClearReminder={() => clearReminder(t.id)}
                  onSetSubtasks={(subs, opts) => setSubtasks(t.id, subs, opts)}
                  onSetArchived={(v) => setArchived(t.id, v)}
                  onReassign={(uid) => reassignTask(t.id, uid)}
                  onTransferCompany={(cid) => transferTaskToCompany(t.id, cid)}
                  onPromoteSubtask={(subId, moveChildIds) => promoteSubtask(t.id, subId, moveChildIds)}
                  onDemoteToSubtask={() => demoteToSubtask(t.id)}
                  onDemoteChild={(childId) => demoteToSubtask(childId)}
                  onPinNumber={(number) => setTaskPin(t.id, true, number)}
                  onUnpinNumber={() => setTaskPin(t.id, false, null)}
                  promotedChildren={promotedChildrenMap[t.id] || []}
                  highlight={searchTerm}
                  isTeamView={isTeamView}
                  isHighlighted={highlightedTaskId === t.id}
                  categoryName={categories.find((c) => c.id === t.category_id)?.name || null}
                  categories={categories}
                  onSetCategory={(cid) => setTaskCategory(t.id, cid)}
                  reminderConfig={reminderConfig}
                  onSetReminderDays={(d) => setTaskReminderDays(t.id, d)}
                  onSetReminderDisabled={(v) => setTaskReminderDisabled(t.id, v)}
                  currentUser={user}
                  onLockChanged={() => { load(); notifyTasksChanged(); }}
                  onLinkTasks={() => setLinkModal({ mode: "create", taskId: t.id })}
                  onEditGroup={() => t.group_id && setLinkModal({ mode: "edit", groupId: t.group_id })}
                  onRemoveFromGroup={() => t.group_id && removeFromGroup(t.group_id, t.id)}
                  onDragToCategory={handleTaskDragToCategory}
                  onDropToCategory={(e, info) => handleTaskDropCategory(t, e, info)}
                  archiveGroup={showArchived ? archiveGroup : null}
                  canPermanentDelete={!!caps.perm_delete}
                  archiveSettings={{ trash_autoclean_enabled: taskSettings.trash_autoclean_enabled, trash_autoclean_days: taskSettings.trash_autoclean_days }}
                  onCancel={() => cancelTask(t.id)}
                  onUncancel={() => uncancelTask(t.id)}
                  onRestore={() => restoreTask(t.id)}
                  onPermanentDelete={() => permanentDeleteTask(t.id)}
                  selectMode={cancelPickMode || taskBulk.selectMode}
                  selected={cancelPickMode ? cancelSel.has(t.id) : taskBulk.has(t.id)}
                  onSelectToggle={() => (cancelPickMode ? toggleCancelSel(t.id) : taskBulk.toggle(t.id))}
                  onSelect={() => taskBulk.start(t.id)}
                  onMoveTask={(id) => taskMove.anaGorevleriTasi(
                    taskBulk.ids?.includes(id) ? [...taskBulk.ids] : [id],
                  )}
                  onMoveSubtasks={(subIds) => taskMove.altGorevleriTasi(t.id, subIds)}
                  onPromoteSubtasks={(subIds) => taskMove.anaGoreveCikar(t.id, subIds)}
                />
              );
            });
          })()}
        </TaskSortableGroup>
      )}

      <div className="hud-text text-sertex-textMuted text-[10px] pt-2 border-t border-sertex-cyan/15 leading-tight px-1">
        💡 Sayaç kutularına tıklayarak filtreleyin (birden fazla seçebilirsiniz) · Kartın sağındaki <span className="text-sertex-cyan">⋮</span> ile düzenle · hatırlat · Kartların sağ-alt köşesinden <span className="text-sertex-cyan">↘</span> tutup boyutlandırın · Alt göreve sağ tık / <span className="text-sertex-cyan">⋮</span> ile durum / tarih ekle · Alt görev başındaki <span className="text-sertex-cyan">⋮⋮</span> ile sırayı değiştirin · Alt görev kutusunun sağ-alt köşesinden <span className="text-sertex-cyan">↘</span> tutup boyutlandırın · Kart sol-üstündeki <span className="text-sertex-cyan">⋮⋮</span> ile görev sırasını değiştirin (kategori filtresi açıkken de aktif)
      </div>

      <MoveTargetModal
        open={!!taskMove.istek}
        onClose={taskMove.kapat}
        onPick={taskMove.hedefSecildi}
        tasks={tasks}
        excludeIds={taskMove.istek?.exclude || []}
        recentIds={taskMove.istek?.recentIds || []}
        title={taskMove.istek?.baslik || "GÖREVİ TAŞI"}
        info={taskMove.istek?.bilgi || ""}
        allowRoot={!!taskMove.istek?.allowRoot}
        busy={taskMove.busy}
      />
      {editQueue.aktif && editQueue.aktifGorev && (        <EditTaskModal
          key={editQueue.aktifGorev.id}
          task={editQueue.aktifGorev}
          onClose={() => {}}
          onSave={(patch) => saveEditFor(editQueue.aktifGorev.id, patch)}
          isTeamView={isTeamView}
          categories={categories}
          teamMembers={teamMembers}
          currentUser={user}
          queue={editQueue.queueProps}
        />
      )}

      {editing && (
        <EditTaskModal
          task={editing}
          onClose={() => { setEditing(null); setEditTaslakOto(false); }}
          onSave={saveEdit}
          isTeamView={isTeamView}
          categories={categories}
          teamMembers={teamMembers}
          currentUser={user}
          taslakOtoYukle={editTaslakOto}
          onTaslakGeriYukle={(tid) => {
            const t = tasks.find((x) => x.id === tid);
            if (t) { setEditTaslakOto(true); setEditing(t); }
          }}
        />
      )}
      {/* Görev Kopyalama — iş koluna Yapıştır menüsü (kopyalama tek tıkla panoya alır) */}
      {pasteMenu && clipboard?.sourceId && (
        <TaskPasteMenu
          x={pasteMenu.x}
          y={pasteMenu.y}
          title={clipboard.title}
          targetName={pasteMenu.categoryName || "Kolsuz"}
          onPaste={() => handlePaste(pasteMenu.categoryId, pasteMenu.categoryName)}
          onClear={() => clearTaskClipboard()}
          onClose={() => setPasteMenu(null)}
        />
      )}
      {catMenu && (
        <CategoryContextMenu
          x={catMenu.x}
          y={catMenu.y}
          categoryName={catMenu.name}
          detached={detachedCatPanels.has(catMenu.id)}
          clipboardTitle={clipboard?.sourceId ? clipboard.title : null}
          onDetach={() => openDetachedPanel({ category: catMenu.id, categoryName: catMenu.name })}
          onDock={() => {
            const pid = detachedCatPanels.get(catMenu.id);
            if (pid) closeDetachedPanel(pid);
          }}
          onPaste={() => handlePaste(catMenu.id, catMenu.name)}
          onClearClipboard={() => clearTaskClipboard()}
          onClose={() => setCatMenu(null)}
        />
      )}
      {templatesOpen && (
        <TemplatesModal
          categories={categories}
          currentUser={user}
          onUse={handleUseTemplate}
          onClose={() => { setTemplatesOpen(false); setTemplatesRefresh((x) => x + 1); }}
        />
      )}
      {/* GÖREV BAĞLAMA — görevleri bağla / grubu düzenle modalı */}
      {linkModal && (
        <LinkTasksModal
          candidateTasks={linkCandidates.candidates}
          preselectedIds={linkCandidates.preselected}
          group={linkCandidates.group}
          onClose={() => setLinkModal(null)}
          onSaved={() => { load(); notifyTasksChanged(); }}
        />
      )}
      {/* Faz 9 CP4.33 — global unlock modal fired from NotificationBell */}
      {pendingUnlockTask && (
        <UnlockOtpModal
          task={pendingUnlockTask}
          onClose={() => setPendingUnlockTask(null)}
          onVerified={() => { load(); notifyTasksChanged(); setPendingUnlockTask(null); }}
        />
      )}
      {/* Task 4 — dışarı alınan görevler büyük yüzen pencerede */}
      {Array.from(detachedIds).map((id, idx) => {
        const t = tasks.find((x) => x.id === id);
        if (!t) return null;
        return (
          <DetachedTaskWindow key={id} task={t} index={idx} onDock={() => toggleDetach(id)}>
            <DetachedTaskCardDnd
              onSubtaskDrag={handleSubtaskDrag}
              onDetachSubtask={(cid, pt) => detachSubByDrag(cid, pt)}
              onDropIntoTask={surukleyerekTasi}
              invalidDropIdsFor={gecersizBirakmaIdleri}
              dragCountOf={surukleSayisi}
              kapsam={`pencere:${id}`}
            >
            {/* FAZ 3 — pencereler arası hedef: ayrılmış kartın ANA görevi de
                "＋İÇİNE" noktası gösterir (kendi içinden sürüklemede değil). */}
            <div className="relative">
            <NestDropDot taskId={id} />
            <TaskCard
              task={t}
              displayNumber={null}
              detached
              onToggleDetach={() => toggleDetach(id)}
              onStatusChange={(status) => setStatus(t.id, status)}
              onDelete={() => { toggleDetach(t.id); removeTask(t.id); }}
              onEdit={() => setEditing(t)}
              onSetReminder={(iso, opts) => setReminder(t.id, iso, opts)}
              onClearReminder={() => clearReminder(t.id)}
              onSetSubtasks={(subs, opts) => setSubtasks(t.id, subs, opts)}
              onSetArchived={(v) => setArchived(t.id, v)}
              onReassign={(uid) => reassignTask(t.id, uid)}
              onTransferCompany={(cid) => transferTaskToCompany(t.id, cid)}
              onPromoteSubtask={(subId, moveChildIds) => promoteSubtask(t.id, subId, moveChildIds)}
              onDemoteToSubtask={() => demoteToSubtask(t.id)}
              onDemoteChild={(childId) => demoteToSubtask(childId)}
              onPinNumber={(number) => setTaskPin(t.id, true, number)}
              onUnpinNumber={() => setTaskPin(t.id, false, null)}
              promotedChildren={promotedChildrenMap[t.id] || []}
              highlight={searchTerm}
              isHighlighted={highlightedTaskId === t.id}
              isTeamView={isTeamView}
              categoryName={categories.find((c) => c.id === t.category_id)?.name || null}
              categories={categories}
              onSetCategory={(cid) => setTaskCategory(t.id, cid)}
              reminderConfig={reminderConfig}
              onSetReminderDays={(d) => setTaskReminderDays(t.id, d)}
              onSetReminderDisabled={(v) => setTaskReminderDisabled(t.id, v)}
              onToggleDigestMute={(v) => setTaskDigestMuted(t.id, v)}
              currentUser={user}
              onLockChanged={() => { load(); notifyTasksChanged(); }}
              detachedSubIds={detachedSubIds}
              onDockSubtask={(cid) => toggleSubDetach(cid)}
              onDetachSubtask={(cid) => detachSubByDrag(cid, null)}
              onCancel={() => cancelTask(t.id)}
              onUncancel={() => uncancelTask(t.id)}
              onPermanentDelete={() => permanentDeleteTask(t.id)}
              selectMode={cancelPickMode || taskBulk.selectMode}
              selected={cancelPickMode ? cancelSel.has(t.id) : taskBulk.has(t.id)}
              onSelectToggle={() => (cancelPickMode ? toggleCancelSel(t.id) : taskBulk.toggle(t.id))}
              onSelect={() => taskBulk.start(t.id)}
              onMoveTask={(id) => taskMove.anaGorevleriTasi(
                taskBulk.ids?.includes(id) ? [...taskBulk.ids] : [id],
              )}
              onMoveSubtasks={(subIds) => taskMove.altGorevleriTasi(t.id, subIds)}
              onPromoteSubtasks={(subIds) => taskMove.anaGoreveCikar(t.id, subIds)}
            />
            </div>
            </DetachedTaskCardDnd>
          </DetachedTaskWindow>
        );
      })}
      {/* AŞAMA 9 (Adım 2) — dışarı alınan alt görevler yüzen pencerede */}
      {Array.from(detachedSubIds).map((subId, idx) => {
        const owner = tasks.find((t) => findSubById(t.subtasks || [], subId));
        if (!owner) return null;
        const node = findSubById(owner.subtasks || [], subId);
        if (!node) return null;
        return (
          <DetachedSubtaskWindow
            key={subId}
            subId={subId}
            title={node.text}
            taskTitle={owner.title}
            index={detachedIds.size + detachedGroupIds.size + idx}
            onDock={() => toggleSubDetach(subId)}
          >
            <DetachedSubtaskContent
              task={owner}
              sub={node}
              onSetSubtasks={(subs, opts) => setSubtasks(owner.id, subs, opts)}
              onSubtaskDrag={handleSubtaskDrag}
              highlight={searchTerm}
              detachedSubIds={detachedSubIds}
              onDetachSubtask={(cid, pt) => detachSubByDrag(cid, pt)}
              onDockSubtask={(cid) => toggleSubDetach(cid)}
              onDropIntoTask={surukleyerekTasi}
              invalidDropIdsFor={gecersizBirakmaIdleri}
              dragCountOf={surukleSayisi}
              kapsam={`altpencere:${subId}`}
            />
          </DetachedSubtaskWindow>
        );
      })}
      {/* GÖREV BAĞLAMA — dışarı alınan bağlı gruplar tek büyük pencerede */}
      {Array.from(detachedGroupIds).map((gid, idx) => {
        const group = groupById[gid];
        if (!group) return null;
        const members = sorted.filter((t) => t.group_id === gid);
        if (members.length === 0) return null;
        const doneCount = members.filter((t) => t.status === "done").length;
        return (
          <DetachedGroupWindow
            key={gid}
            group={group}
            doneCount={doneCount}
            total={members.length}
            index={detachedIds.size + idx}
            onDock={() => toggleGroupDetach(gid)}
          >
            <Reorder.Group
              axis="y"
              values={members}
              onReorder={(nt) => handleReorderGroup(gid, nt)}
              className="space-y-2"
            >
              {members.map((t) => (
                <GroupWindowMemberRow key={t.id} task={t} cardProps={cardPropsFor(t)} />
              ))}
            </Reorder.Group>
          </DetachedGroupWindow>
        );
      })}
    </div>
  );
};

export default TasksPanel;
